import React, { useEffect, useState, useRef, forwardRef, useMemo } from "react";
import { FaInfoCircle, FaExclamationTriangle } from "react-icons/fa";
import { BsChevronDown } from "react-icons/bs";
import { BiChevronDown } from "react-icons/bi";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { Prompt } from "react-router-dom";
import DatePicker from "react-datepicker";
import { careTypes } from "../../utils/data";
import Footer from "../../components/footer";
import FooterButton from "../../components/footerButton";
import { FiChevronRight, FiTrash2, FiArrowRight } from "react-icons/fi";
import PickDate from "../../components/common/datePicker";
import FunctionIssues from "../../services/plan_service";
import { getScore } from "../../services/plan_service";
import {
  getScoreOptionPillProps,
  scaleResolverFor,
} from "../../utils/scoringScale";
import InputDatePicker from "../../components/common/inputDatePicker";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import GetFunctionalSummary from "../../hooks/getFunctionalSummary";
import GetInterventions from "../../hooks/getInterventions";
import GetMeetingTypes from "../../hooks/getMeetingTypes";
import GetPositions from "../../hooks/getPositions";
import GetUsers from "../../hooks/getUsers";
import GetPatientCareProviders from "../../hooks/getPatientCareProviders";
import AddCarePlan from "../../hooks/createCarePlan";
import UpdateCarePlan from "../../hooks/updateCarePlan";
import AmendCarePlan from "../../hooks/amendCarePlan";
import useCarePlanInitialScore from "../../hooks/getCarePlanInitialScore";
import useCarePlanCurrentScore from "../../hooks/getCarePlanCurrentScore";
import useCarePlanMedianScore from "../../hooks/getCarePlanMedianScore";
import { useQueryClient } from "@tanstack/react-query";
import { Bounce, toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import moment from "moment";
import TeamScoresOverlay from "../../components/hcp/TeamScoresOverlay";
import LatestFunctionalScoresOverlay from "../../components/hcp/LatestFunctionalScoresOverlay";
import {
  getRemovedBaselineSubheadings,
  addRemovedBaselineSubheading,
  restoreRemovedBaselineSubheading,
  clearRemovedBaselineSubheadings,
} from "../../utils/removedBaselineSubheadings";

const ICON_SIZE = 20;
const FAMILY_POSITION = {
  id: null,
  name: "Family Member",
  abbreviation: "Fam",
  assigneeType: "FAMILY",
};
const PATIENT_POSITION = {
  id: null,
  name: "Patient",
  abbreviation: "Pt",
  assigneeType: "PATIENT",
};
const SPECIAL_ASSIGNEE_POSITIONS = [FAMILY_POSITION, PATIENT_POSITION];

const getPersonName = (person) =>
  `${person?.lastName || ""} ${person?.firstName || ""}`.trim();

const normalizeLinkedPerson = (person, position) => ({
  ...person,
  id: person?.id || person?.patientId || person?.contactId || null,
  firstName: person?.firstName || "",
  lastName: person?.lastName || "",
  position,
  assigneeType: position.assigneeType,
});

const getPatientFamilyMembers = (patient) => {
  const familySources = [
    patient?.familyMembers,
    patient?.family,
    patient?.emergencyContacts,
    patient?.contacts,
  ];

  return familySources
    .find((source) => Array.isArray(source))
    ?.map((member) => normalizeLinkedPerson(member, FAMILY_POSITION)) || [];
};

const getProviderDropdownPrompt = (position, providers) => {
  if (providers.length > 0) return "Choose one provider only:";
  if (position?.assigneeType === "FAMILY") return "No linked family members";
  if (position?.assigneeType === "PATIENT") return "No linked patient";
  return "Choose position first";
};

const Item1 = ({ onClear }) => (
  <button className="btn btn_danger px-5" onClick={onClear}>
    <small>CLEAR</small>
  </button>
);

const Item2 = ({
  props,
  date,
  functions,
  handleCarePlan,
  carePlan,
  type,
  meeting,
  meetingTypeId,
  attendees,
  onSaveForLater,
  onReview,
  carePlanId,
  editReason,
  carePlanStatus,
  carePlanVersion,
  fromTeamScores,
  teamScoreFromDate,
  teamScoreToDate,
}) => {
  const hasAtLeastOneFunctionalIssue = carePlan?.some((heading) =>
    heading.functional_issues?.some((issue) => issue.subheading),
  );

  const handleReview = () => {
    if (!hasAtLeastOneFunctionalIssue) {
      toast.warn("Please select a functional issue", {
        position: "top-right",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }
    onReview(); // Allow navigation
    props.history.push({
      pathname: `/hcp/patient/care_review_plan`,
      state: {
        date,
        carePlan,
        type,
        meeting,
        meetingTypeId,
        attendees,
        carePlanId,
        editReason,
        carePlanStatus,
        carePlanVersion,
        fromTeamScores,
        fromDate: teamScoreFromDate,
        toDate: teamScoreToDate,
      },
    });
  };

  const handleSaveForLater = () => {
    if (!hasAtLeastOneFunctionalIssue) {
      toast.warn("Please select a functional issue", {
        position: "top-right",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }
    onSaveForLater(); // Allow navigation
    handleCarePlan();
  };

  return (
    <div>
      <button
        className="btn btn_outline_light px-3 mx-3"
        onClick={handleSaveForLater}
      >
        <small>SAVE FOR LATER</small>
      </button>
      <button onClick={handleReview} className="btn btn_light pl-4 pr-2 ml-3">
        <small className="mx-3">REVIEW</small>
        <FiChevronRight size={20} />
      </button>
    </div>
  );
};

const MeetingEntryDateInput = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    className={`d-flex align-items-center justify-content-center w-100 px-3 py-2 rounded ${
      value ? "datePicker-active" : "datePicker"
    }`}
    style={{ height: "42px", backgroundColor: "#fff", position: "relative" }}
    onClick={onClick}
    ref={ref}
  >
    <small className={value ? "text_primary" : "text-muted"}>
      {value || "Choose Date"}
    </small>
    <i
      className="fa fa-calendar text_primary"
      style={{ position: "absolute", right: "12px" }}
    />
  </button>
));

const CareManagement = (props) => {
  const [carePlan, setCarePlan] = useState(null);
  const [editReason, setEditReason] = useState(
    props.location.state?.editReason || "",
  );
  // The care-plan body (type selection, headings) is revealed only after the
  // user submits the reason via the arrow button. A reason round-tripped from
  // navigation counts as already confirmed.
  const [editReasonConfirmed, setEditReasonConfirmed] = useState(
    Boolean(props.location.state?.editReason?.trim()),
  );
  const [listOfAttendees, setListOfAttendees] = useState(false);
  const [date, setDate] = useState(
    props.location.state ? props.location.state.date : null,
  );
  const [chosenDate, setChosenDate] = useState(
    props.location.state ? props.location.state.date : null,
  );

  // Navigation confirmation states
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [hasDataBeenEntered, setHasDataBeenEntered] = useState(false);

  // Clear button confirmation state
  const [showClearModal, setShowClearModal] = useState(false);
  const [showClearRowModal, setShowClearRowModal] = useState(false);
  const [clearRowAction, setClearRowAction] = useState(null);
  const [clearRowModalContent, setClearRowModalContent] = useState({
    title: "Are you sure you want to clear this row?",
    note: "All scores and comments will be lost.",
  });

  // Accordion expanded state. Seed with the first heading so it is open at the
  // Accordion's initial mount — `preExpanded` is only read once on mount, and
  // in the from-review flow date/type/attendees/carePlan all arrive together,
  // so the "open first accordion" effect runs too late to take effect.
  const [expandedAccordions, setExpandedAccordions] = useState(["accordion-0"]);

  // Team score overlay state
  const [showTeamScoreOverlay, setShowTeamScoreOverlay] = useState(false);
  const [showLatestScoresOverlay, setShowLatestScoresOverlay] = useState(false);

  // Check if coming from review page
  const fromReview = props.location.state?.fromReview;
  const reviewCarePlan = props.location.state?.carePlan;
  const reviewType = props.location.state?.type;
  const reviewMeeting = props.location.state?.meeting;
  const reviewMeetingTypeId = props.location.state?.meetingTypeId;
  const reviewAttendees = props.location.state?.attendees;
  const reviewCarePlanId = props.location.state?.carePlanId;
  // Versioning: when the plan being edited originated as SIGNED, saving here
  // must amend (create a new version) rather than edit in place.
  const reviewCarePlanStatus = props.location.state?.carePlanStatus;
  const reviewCarePlanVersion = props.location.state?.carePlanVersion;
  const isAmendMode = reviewCarePlanStatus === "SIGNED";
  // A reason is required — and the care-plan headings stay hidden until it is
  // entered — when amending a signed plan or editing any version > 1.
  const requiresEditReason = isAmendMode || reviewCarePlanVersion > 1;
  const showCarePlanBody = !requiresEditReason || editReasonConfirmed;

  // Check if coming from team scores submission
  const fromTeamScores = props.location.state?.fromTeamScores;
  const preselectedType = props.location.state?.preselectedType;
  const teamScoreFromDate = props.location.state?.fromDate;
  const teamScoreToDate = props.location.state?.toDate;
  // Set on Functional Summaries when the user chose there to stand the medians
  // in for the team scores. The decision is already made by the time we get
  // here, so a collaborative plan applies it silently — no toggle on this page.
  // The team-score batch the medians were written to on Functional Summaries.
  // Stored on the plan so reopening it resolves that exact batch rather than
  // inferring one from dates.
  const teamScoreBatchId = props.location.state?.teamScoreBatchId;
  const patientId = localStorage.getItem("patient");
  // The logged-in care provider — the author of anything created here.
  const currentProviderId = useMemo(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("user"))?.organizations?.[0]
          ?.careProvider?.id ?? null
      );
    } catch {
      return null;
    }
  }, []);
  const outstandingCarePlanId = localStorage.getItem("outstandingCarePlanId");
  // Autopopulation (auto-adding baseline subheading rows and auto-filling
  // current scores) only helps when filling a brand-new care plan. A care plan
  // that was already saved (outstanding draft, or signed/amend) carries an id,
  // so we render only its saved issues and never auto-add or auto-fill current.
  const isFreshCarePlan = !(reviewCarePlanId || outstandingCarePlanId);
  const teamScoreRange = useMemo(() => {
    // Prefer the dates carried over from the team-scores flow. When they are
    // absent (e.g. opening an existing care plan from Care Plan Summaries and
    // editing it) anchor on the care plan's own date so the team scores shown
    // are the ones for that plan's period, not simply the last 7 days.
    const anchorEnd =
      date && moment(date).isValid()
        ? moment(date).startOf("day")
        : moment().startOf("day");
    const rangeEnd =
      teamScoreToDate && moment(teamScoreToDate).isValid()
        ? moment(teamScoreToDate).startOf("day")
        : anchorEnd;
    const rangeStart =
      teamScoreFromDate && moment(teamScoreFromDate).isValid()
        ? moment(teamScoreFromDate).startOf("day")
        : moment(rangeEnd).subtract(7, "days").startOf("day");
    return {
      start: rangeStart,
      end: rangeEnd,
      startDate: rangeStart.format("YYYY-MM-DDTHH:mm:ss.SSS"),
      endDate: rangeEnd.format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };
  }, [teamScoreFromDate, teamScoreToDate, date]);
  // Track if we've already initialized from review data
  const initializedFromReview = useRef(false);

  const queryClient = useQueryClient();

  const {
    mutate,
    isLoading: createLoading,
    isSuccess,
    error: createError,
  } = AddCarePlan();
  const { mutate: updateMutate, isSuccess: updateSuccess } = UpdateCarePlan();
  const { mutate: amendMutate } = AmendCarePlan();
  const { data, isLoading, error } = GetFunctionalChecklist();

  // Patients can have more than one checklist assigned; the active one is
  // the one currently in use for care planning, falling back to the first
  // entry if none is flagged active.
  const activeChecklist = useMemo(
    () => data?.data?.find((item) => item.active) ?? data?.data?.[0],
    [data]
  );

  // The scale each row is scored on: the care-plan payload carries the
  // subheading but not the range it was scored on, so it is resolved from the
  // checklist heading the subheading belongs to.
  const resolveScale = scaleResolverFor(activeChecklist?.headings || []);
  const {
    data: teamScoreData,
    isLoading: isTeamScoreLoading,
    error: teamScoreError,
  } = GetFunctionalSummary(
    patientId,
    teamScoreRange.startDate,
    teamScoreRange.endDate,
    null,
    null,
    null,
    null,
    null,
    true,
  );
  const {
    data: interventions,
    isLoading: isLoadingInterv,
    error: errorInterv,
  } = GetInterventions();
  const { data: meetingTypesData } = GetMeetingTypes();
  const {
    data: positions,
    isLoading: isLoadingPostions,
    error: errorPositions,
  } = GetPositions();
  const {
    data: organizationHcps,
    isLoading: isLoadingProviders,
    error: errorProviders,
  } = GetUsers();
  // Attendees are the patient's own collaborators, not everyone in the
  // organization. `organizationHcps` above still backs the intervention
  // assignee dropdown, which is a different question.
  const { data: patientCareProviders } = GetPatientCareProviders();
  const [type, setType] = useState(
    fromReview && reviewType
      ? reviewType
      : fromTeamScores && preselectedType
        ? preselectedType
        : localStorage.getItem("type"),
  );

  // Past-week range for the Functional Scores overlay (last 7 days).
  const functionalScoresWeekRange = useMemo(() => {
    const end = moment().endOf("day");
    const start = moment().subtract(6, "days").startOf("day");
    return {
      startDate: start.format("YYYY-MM-DDTHH:mm:ss.SSS"),
      endDate: end.format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };
  }, []);
  const functionalScoresOverlayRange = fromTeamScores
    ? {
        startDate: teamScoreRange.startDate,
        endDate: teamScoreRange.endDate,
      }
    : functionalScoresWeekRange;

  // Functional Summaries tab data: all scores for the past week, final
  // (current) versions only — no historical versions.
  const {
    data: functionalScoresData,
    isLoading: isLatestScoresLoading,
    error: latestScoresError,
  } = GetFunctionalSummary(
    patientId,
    functionalScoresOverlayRange.startDate,
    functionalScoresOverlayRange.endDate,
    null,
    null,
    null,
    null,
    null,
    false, // includeTeamScores
    false, // includeHistory — only final versions
  );

  const functionalScoresWithTeamScores = useMemo(() => {
    const teamScoresBySubheadingId = new Map();
    (teamScoreData?.data || []).forEach((heading) => {
      (heading.subheadings || []).forEach((subheading) => {
        if (subheading?.id) {
          teamScoresBySubheadingId.set(subheading.id, {
            median: subheading.median,
            teamScore: subheading.teamScore,
          });
        }
      });
    });

    return {
      ...(functionalScoresData || {}),
      data: (functionalScoresData?.data || []).map((heading) => ({
        ...heading,
        subheadings: (heading.subheadings || []).map((subheading) => ({
          ...subheading,
          ...(teamScoresBySubheadingId.get(subheading.id) || {}),
        })),
      })),
    };
  }, [functionalScoresData, teamScoreData]);

  // Initial and current scores come from two separate endpoints. For
  // INDIVIDUAL both are scoped to the logged-in provider's own assessments.
  const { data: initialScoreData } = useCarePlanInitialScore(
    patientId,
    type?.value,
  );
  const { data: currentScoreData } = useCarePlanCurrentScore(
    patientId,
    type?.value,
    fromTeamScores ? teamScoreRange.start.format("YYYY-MM-DD") : null,
    fromTeamScores ? teamScoreRange.end.format("YYYY-MM-DD") : null,
  );

  const localPatient = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("localPatient") || "{}");
    } catch (error) {
      return {};
    }
  }, []);

  const getProvidersForPosition = (position) => {
    if (!position) return [];

    if (position.assigneeType === "PATIENT") {
      return localPatient?.id || localPatient?.firstName || localPatient?.lastName
        ? [normalizeLinkedPerson(localPatient, PATIENT_POSITION)]
        : [];
    }

    if (position.assigneeType === "FAMILY") {
      return getPatientFamilyMembers(localPatient);
    }

    return (
      organizationHcps?.data?.content?.filter(
        (item) => item.position.name === position.name,
      ) || []
    );
  };
  // Merge the two endpoints back into the one entry-per-subheading shape the
  // rest of the page consumes. A subheading with no answer is absent from
  // either map, so an entry may carry an initial without a current or vice
  // versa — unlike the deprecated combined endpoint, neither stands in for the
  // other.
  const baselineScores = useMemo(() => {
    const initial = initialScoreData?.data || {};
    const current = currentScoreData?.data || {};
    const subheadingIds = new Set([
      ...Object.keys(initial),
      ...Object.keys(current),
    ]);
    if (subheadingIds.size === 0) return null;
    const checklistSubheadingIds = new Set(
      (data?.data || []).flatMap((checklist) =>
        (checklist.headings || []).flatMap((heading) =>
          (heading.subheadings || []).map((sub) => sub.id)
        )
      )
    );
    const merged = {};
    subheadingIds.forEach((id) => {
      if (!checklistSubheadingIds.has(id)) return;
      merged[id] = {
        initialScore: initial[id]?.score ?? null,
        initialAssessmentDate: initial[id]?.assessmentDate ?? null,
        currentScore: current[id]?.score ?? null,
        currentAssessmentDate: current[id]?.assessmentDate ?? null,
      };
    });
    return Object.keys(merged).length > 0 ? merged : null;
  }, [initialScoreData, currentScoreData, data]);

  const medianScores = useMemo(() => {
    if (type?.value !== "COLLABORATIVE") return {};
    const result = {};
    (teamScoreData?.data || []).forEach((heading) => {
      (heading.subheadings || []).forEach((sub) => {
        if (sub.id && sub.median > 0) {
          result[sub.id] = Math.floor(sub.median);
        }
      });
    });
    return result;
  }, [teamScoreData, type?.value]);

  const normalizeBaselineEntry = (entry) => {
    if (!entry) return null;
    return {
      initial_score: entry.initialScore != null ? (Number(entry.initialScore) || entry.initialScore) : undefined,
      initial_score_date: entry.initialAssessmentDate ? new Date(entry.initialAssessmentDate) : undefined,
      current_score: entry.currentScore != null ? (Number(entry.currentScore) || entry.currentScore) : undefined,
      current_score_date: entry.currentAssessmentDate ? new Date(entry.currentAssessmentDate) : undefined,
    };
  };

  // --- Median as current / team score -------------------------------------
  // The median across every provider who assessed the patient can stand in for
  // the current score. On an individual plan the provider picks the period and
  // switches it on here. On a collaborative plan the choice is made back on
  // Functional Summaries, and this page simply honours it over the plan's own
  // team-score period.
  const isIndividualPlan = type?.value === "INDIVIDUAL";
  const isCollaborativePlan = type?.value === "COLLABORATIVE";
  const [showTeamScoreRedirectModal, setShowTeamScoreRedirectModal] =
    useState(false);
  // The type the user picked, held until they confirm losing the current rows.
  const [pendingType, setPendingType] = useState(null);
  const [medianPickerOpen, setMedianPickerOpen] = useState(false);
  // The "what does My Scores mean?" note next to the toggle.
  const [scoreSourceInfoOpen, setScoreSourceInfoOpen] = useState(false);
  const [pickedMedianRange, setPickedMedianRange] = useState(null);
  const [draftMedianRange, setDraftMedianRange] = useState({
    from: null,
    to: null,
  });
  const [medianMode, setMedianMode] = useState(false);
  // The scores that were on the rows beforehand, kept so the substitution can
  // be undone — it overwrites every row, including ones the team did score.
  const preMedianCurrentRef = useRef(null);
  // Rows the median has already been written to. A row is filled once and then
  // left alone, so a score can still be overridden without being pulled back.
  const medianFilledRowsRef = useRef(new Set());
  // Rows the median substitution added to the plan, so reverting can take them
  // back out again rather than leaving empty rows behind.
  const medianAddedRowIdsRef = useRef(new Set());
  const medianRowsAddedRef = useRef(false);

  // The period the median covers: the plan's own team-score period when
  // collaborative, the range the provider picked when individual.
  const medianRange = useMemo(() => {
    if (isCollaborativePlan) {
      return {
        from: teamScoreRange.start.toDate(),
        to: teamScoreRange.end.toDate(),
      };
    }
    return pickedMedianRange;
  }, [isCollaborativePlan, teamScoreRange, pickedMedianRange]);

  // The Current Score column heading names whatever is actually filling the
  // column, so switching the source above renames it here.
  const currentScoreColumnLabel = medianMode
    ? "Current Median Score"
    : isCollaborativePlan
      ? fromTeamScores
        ? "Current Team Score"
        : "Current Score"
      : "Current Ind Score";

  const { data: medianScoreData, isLoading: isMedianLoading } =
    useCarePlanMedianScore(
      patientId,
      medianRange?.from ? moment(medianRange.from).format("YYYY-MM-DD") : null,
      medianRange?.to ? moment(medianRange.to).format("YYYY-MM-DD") : null,
      medianMode && (isIndividualPlan || isCollaborativePlan),
    );

  // { subheadingId: { median, sampleSize } } — medians arrive already floored
  // to the 1-5 scale, so never re-round them here.
  const rangeMedianScores = useMemo(() => {
    const result = {};
    Object.entries(medianScoreData?.data || {}).forEach(([id, entry]) => {
      if (entry?.median != null) {
        result[id] = { median: entry.median, sampleSize: entry.sampleSize };
      }
    });
    return result;
  }, [medianScoreData]);

  const rowKey = (headingId, issueId) => `${headingId}::${issueId}`;
  // The subheading is part of the key so that re-pointing a row at a different
  // functional issue counts as a new row and picks up that issue's median.
  const medianRowKey = (headingId, issue) =>
    `${headingId}::${issue?.id}::${issue?.subheading?.id}`;

  // The median describes the period, so date the score at the end of it.
  const medianScoreDate = useMemo(
    () => (medianRange?.to ? new Date(medianRange.to) : new Date()),
    [medianRange],
  );

  const applyMedianToRow = (issue) => {
    const median = issue.subheading?.id
      ? rangeMedianScores[issue.subheading.id]
      : null;
    if (!median) {
      // Nobody scored this subheading over the period. On a collaborative plan
      // the row may still hold a real team score, so leave it; on an individual
      // plan the column is showing medians and a blank says "no data".
      return isCollaborativePlan
        ? issue
        : { ...issue, current_score: null, current_score_date: null };
    }
    return {
      ...issue,
      current_score: median.median,
      current_score_date: medianScoreDate,
    };
  };

  // A median over a sample of one is just that clinician's score, and the
  // number alone cannot say so — surface the sample size on the row.
  const currentScoreTitle = (item) => {
    if (isCurrentScoreLocked(item))
      return "Current score is fixed for this admission";
    if (!medianMode) return undefined;
    const median = item.subheading?.id
      ? rangeMedianScores[item.subheading.id]
      : null;
    if (!median) return "No scores recorded over this period";
    return median.sampleSize === 1
      ? "Median of 1 score — this is a single provider's score"
      : `Median of ${median.sampleSize} scores`;
  };

  // Leave for Functional Summaries to assign team scores. The unsaved-work
  // Prompt would otherwise block this, so clear it first — nothing has been
  // entered yet, the user only opened the type dropdown.
  const handleGoToTeamScores = () => {
    setShowTeamScoreRedirectModal(false);
    setAllowNavigation(true);
    sessionStorage.removeItem("hasUnsavedCarePlan");
    props.history.push("/hcp/patient/summary_functions");
  };

  // The info icon lives inside the My Scores option, so the event is stopped
  // short of the button that would otherwise change the score source.
  const handleToggleScoreSourceInfo = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setMedianPickerOpen(false);
    setScoreSourceInfoOpen((open) => !open);
  };

  const handleOpenMedianPicker = () => {
    setDraftMedianRange({
      from: pickedMedianRange?.from || teamScoreRange.start.toDate(),
      to: pickedMedianRange?.to || teamScoreRange.end.toDate(),
    });
    setScoreSourceInfoOpen(false);
    setMedianPickerOpen(true);
  };

  // Individual plans: apply over the range picked in the dropdown.
  const handleApplyMedian = () => {
    const { from, to } = draftMedianRange;
    if (from && to && moment(from).isAfter(moment(to))) {
      toast.warn("The start date must be on or before the end date", {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
        transition: Bounce,
      });
      return;
    }
    setPickedMedianRange({ from, to });
    setMedianPickerOpen(false);
    // A new range refills every row, including ones already overridden.
    handleConfirmMedian();
  };

  // Applying is destructive, so it is only reached once the period is settled —
  // through the range picker on an individual plan, or through the choice the
  // user already confirmed on Functional Summaries on a collaborative one.
  const handleConfirmMedian = () => {
    if (!preMedianCurrentRef.current && carePlan) {
      const snapshot = {};
      carePlan.forEach((careheading) => {
        careheading.functional_issues.forEach((issue) => {
          snapshot[rowKey(careheading?.id, issue?.id)] = {
            current_score: issue.current_score,
            current_score_date: issue.current_score_date,
          };
        });
      });
      preMedianCurrentRef.current = snapshot;
    }
    medianFilledRowsRef.current = new Set();
    medianAddedRowIdsRef.current = new Set();
    medianRowsAddedRef.current = false;
    setMedianMode(true);
  };

  const handleClearMedian = () => {
    const snapshot = preMedianCurrentRef.current;
    const addedRowIds = medianAddedRowIdsRef.current;
    setCarePlan((prev) => {
      if (!prev) return prev;
      return prev.map((careheading) => {
        // Take back the rows the substitution added; a heading left with
        // nothing gets its empty placeholder row back.
        const kept = careheading.functional_issues.filter(
          (issue) => !addedRowIds.has(rowKey(careheading?.id, issue?.id)),
        );
        const restored = (
          kept.length > 0 ? kept : [{ id: 1, intervention: [{}] }]
        ).map((issue) => {
          const saved = snapshot?.[rowKey(careheading?.id, issue?.id)];
          if (saved) {
            return {
              ...issue,
              current_score: saved.current_score,
              current_score_date: saved.current_score_date,
            };
          }
          // Row added while the median was applied — fall back to the score
          // the plan would otherwise have carried for that subheading.
          const normalized = normalizeBaselineEntry(
            baselineScores?.[issue.subheading?.id],
          );
          return {
            ...issue,
            current_score: normalized?.current_score ?? null,
            current_score_date: normalized?.current_score_date ?? null,
          };
        });
        return { ...careheading, functional_issues: restored };
      });
    });
    preMedianCurrentRef.current = null;
    medianFilledRowsRef.current = new Set();
    medianAddedRowIdsRef.current = new Set();
    medianRowsAddedRef.current = false;
    setMedianMode(false);
    setMedianPickerOpen(false);
    setPickedMedianRange(null);
  };

  // Nothing to do for a collaborative plan arriving from Functional Summaries:
  // the medians were written there as the team's actual scores, so they come
  // back through /scores/current like any other team score. Re-applying them
  // here would recompute a client-side median over a possibly different range
  // and paint it over the numbers that were just saved.

  // Push medians onto the rows once the query resolves, and again whenever
  // rows are added. On the first pass every subheading with a median is also
  // added to the plan, so the whole picture is on screen rather than only the
  // issues that happened to be selected already.
  useEffect(() => {
    if (!medianMode || !(isIndividualPlan || isCollaborativePlan) || !carePlan)
      return;
    // Wait for the response. A disabled/just-enabled query reports no data and
    // no loading, and acting on that would fill nothing and mark rows done.
    if (isMedianLoading || !medianScoreData?.data) return;
    const filled = medianFilledRowsRef.current;
    const added = medianAddedRowIdsRef.current;
    // Subheadings deleted earlier in this flow stay deleted — the substitution
    // fills the plan, it doesn't undo the user's own removals.
    const removedSubheadingIds = getRemovedBaselineSubheadings(patientId);
    const addRows = !medianRowsAddedRef.current;
    let planChanged = false;

    const updated = carePlan.map((careheading) => {
      let headingChanged = false;
      let issues = careheading.functional_issues.map((issue) => {
        if (!issue.subheading?.id) return issue;
        const key = medianRowKey(careheading?.id, issue);
        if (filled.has(key)) return issue;
        filled.add(key);
        const next = applyMedianToRow(issue);
        if (next !== issue) headingChanged = true;
        return next;
      });

      if (addRows) {
        const existingSubheadingIds = new Set(
          issues.filter((fi) => fi.subheading?.id).map((fi) => fi.subheading.id),
        );
        const subheadingsToAdd = (careheading.heading.subheadings || []).filter(
          (sub) =>
            rangeMedianScores[sub.id] &&
            !existingSubheadingIds.has(sub.id) &&
            !removedSubheadingIds.has(sub.id),
        );
        if (subheadingsToAdd.length > 0) {
          headingChanged = true;
          const maxId = issues.reduce(
            (max, fi) => Math.max(max, typeof fi.id === "number" ? fi.id : 0),
            0,
          );
          const newIssues = subheadingsToAdd.map((sub, idx) => {
            const normalized = normalizeBaselineEntry(baselineScores?.[sub.id]);
            const id = maxId + idx + 1;
            added.add(rowKey(careheading?.id, id));
            filled.add(`${careheading?.id}::${id}::${sub.id}`);
            return {
              id,
              subheading: sub,
              intervention: [{}],
              ...(normalized?.initial_score != null
                ? { initial_score: normalized.initial_score }
                : {}),
              ...(normalized?.initial_score_date
                ? { initial_score_date: normalized.initial_score_date }
                : {}),
              current_score: rangeMedianScores[sub.id].median,
              current_score_date: medianScoreDate,
            };
          });
          // Drop empty placeholder rows — the median rows replace them.
          issues = [...issues.filter((fi) => fi.subheading?.id), ...newIssues];
        }
      }

      if (!headingChanged) return careheading;
      planChanged = true;
      return { ...careheading, functional_issues: issues };
    });

    if (addRows) medianRowsAddedRef.current = true;
    if (planChanged) setCarePlan(updated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medianMode, isIndividualPlan, isCollaborativePlan, isMedianLoading, medianScoreData, rangeMedianScores, medianScoreDate, carePlan]);

  const resetCarePlanRows = (prev) => {
    if (!prev) return prev;
    return prev.map((careheading) => ({
      ...careheading,
      functional_issues: [{ id: 1, intervention: [{}] }],
    }));
  };

  const populateFromBaseline = (prev, baselines, skipCurrentScore = false) => {
    if (!prev || !baselines) return prev;
    const removedSubheadingIds = getRemovedBaselineSubheadings(patientId);
    let changed = false;
    const updated = prev.map((careheading) => {
      const existingSubheadingIds = new Set(
        careheading.functional_issues
          .filter((fi) => fi.subheading?.id)
          .map((fi) => fi.subheading.id)
      );
      const subheadingsToAdd = (careheading.heading.subheadings || []).filter(
        (sub) =>
          baselines[sub.id] &&
          !existingSubheadingIds.has(sub.id) &&
          !removedSubheadingIds.has(sub.id)
      );
      if (subheadingsToAdd.length === 0) return careheading;
      changed = true;
      const maxId = careheading.functional_issues.reduce(
        (max, fi) => Math.max(max, typeof fi.id === "number" ? fi.id : 0), 0
      );
      const newIssues = subheadingsToAdd.map((sub, idx) => {
        const normalized = normalizeBaselineEntry(baselines[sub.id]);
        return {
          id: maxId + idx + 1,
          subheading: sub,
          intervention: [{}],
          ...(normalized.initial_score != null ? { initial_score: normalized.initial_score } : {}),
          ...(normalized.initial_score_date ? { initial_score_date: normalized.initial_score_date } : {}),
          ...(!skipCurrentScore && normalized.current_score != null ? { current_score: normalized.current_score } : {}),
          ...(!skipCurrentScore && normalized.current_score_date ? { current_score_date: normalized.current_score_date } : {}),
        };
      });
      // Drop empty placeholder rows — baseline rows replace them
      const nonEmptyIssues = careheading.functional_issues.filter(
        (fi) => fi.subheading?.id
      );
      return {
        ...careheading,
        functional_issues: [...nonEmptyIssues, ...newIssues],
      };
    });
    return changed ? updated : prev;
  };

  const isCurrentScoreLocked = (item) => {
    // The admission "fixed current score" lock only applies to a fresh care
    // plan. When editing an existing/amended plan the user must be able to
    // update the current score.
    if (!isFreshCarePlan) return false;
    if (!fromTeamScores && type?.value === "COLLABORATIVE") return false;
    // While the median is applied the value on the row isn't the fixed
    // admission score, so the provider stays free to override it.
    if (medianMode) return false;
    // Simple rule: once a row has both a current score and its date filled in
    // (e.g. auto-populated from baseline), lock it.
    return (
      item.current_score != null &&
      item.current_score !== "" &&
      Boolean(item.current_score_date)
    );
  };

  const [meeting, setMeeting] = useState(
    fromReview && reviewMeeting ? reviewMeeting : null,
  );
  // Meeting type is now backed by a meeting-type row: `meeting` holds the name
  // for display, `meetingTypeId` holds the numeric id sent on the payload.
  const [meetingTypeId, setMeetingTypeId] = useState(
    fromReview && reviewMeetingTypeId != null ? reviewMeetingTypeId : null,
  );
  const [attendees, setAttendees] = useState(
    fromReview && reviewAttendees
      ? reviewAttendees
      : {
          confirmedAttendees: false,
          attendees: [],
        },
  );
  // The patient's collaborators, minus the logged-in provider — they are the
  // author of this care plan, so they are added on save rather than picked.
  const attendeeOptions = useMemo(
    () =>
      (patientCareProviders?.data || []).filter(
        (provider) => provider?.id != null && provider.id !== currentProviderId,
      ),
    [patientCareProviders, currentProviderId],
  );
  const availableAttendeeIds = useMemo(
    () => attendeeOptions.map((attendee) => attendee.id),
    [attendeeOptions],
  );
  const allAvailableAttendeesSelected =
    availableAttendeeIds.length > 0 &&
    availableAttendeeIds.every((id) => attendees.attendees.includes(id));

  // Update listOfAttendees state based on review data
  // Note: We don't automatically open the dropdown when coming from review
  // The dropdown should only open when user clicks "Confirm Attendees"
  useEffect(() => {
    // Ensure dropdown is closed when navigating back from review
    if (fromReview) {
      setListOfAttendees(false);
    }
  }, [fromReview]);

  // Seed the type from the state we arrived with. This runs once: it used to
  // keep `type` in its own dependencies and force the value back on every
  // change, so picking another type reverted instantly — and wiped the rows on
  // the way, because handleType resets them before the revert lands.
  const typeSeeded = useRef(false);
  useEffect(() => {
    if (typeSeeded.current) return;
    if (fromReview && reviewType) {
      setType(reviewType);
      typeSeeded.current = true;
    } else if (fromTeamScores && preselectedType) {
      setType(preselectedType);
      typeSeeded.current = true;
    }
  }, [fromReview, reviewType, fromTeamScores, preselectedType]);

  const { functionIssue, setFunctionIssue } = FunctionIssues();
  const functions = props.location.state;

  // Track if we've already initialized and prevent localStorage clearing when from review
  const initialized = useRef(false);
  const preventLocalStorageClearing = useRef(fromReview);

  useEffect(() => {
    let originalRemoveItem = null;

    if (!initialized.current) {
      if (fromReview && reviewType) {
        // Coming from review - set type in localStorage and mark to prevent clearing
        localStorage.setItem("type", reviewType.value || reviewType);
        preventLocalStorageClearing.current = true;
        // Override localStorage.removeItem to prevent clearing type when from review
        originalRemoveItem = localStorage.removeItem;
        localStorage.removeItem = function (key) {
          if (key === "type" && preventLocalStorageClearing.current) {
            return;
          }
          return originalRemoveItem.call(this, key);
        };
      } else if (fromTeamScores && preselectedType) {
        // Coming from team scores - set collaborative care plan type
        localStorage.setItem("type", preselectedType.value);
        setType(preselectedType);
      } else if (!fromReview && !fromTeamScores) {
        // Not coming from review or team scores - clear localStorage as usual
        localStorage.removeItem("type");
        setType(null);
      }
      initialized.current = true;
    }

    // Cleanup function to restore original localStorage.removeItem
    return () => {
      if (originalRemoveItem) {
        localStorage.removeItem = originalRemoveItem;
      }
    };
  }, [fromReview, reviewType, fromTeamScores, preselectedType]);

  // Separate useEffect for care plan initialization
  useEffect(() => {
    if (date && data && !carePlan) {
      // If coming from review, use the review care plan data (only once)
      if (fromReview && reviewCarePlan && !initializedFromReview.current) {
        // The review page leaves heading.subheadings empty for outstanding/amend
        // plans (it sources the dropdown from the checklist), so hydrate them
        // from the checklist here or the functional-issue dropdown is empty.
        const checklistHeadings = activeChecklist?.headings || [];
        const hydrated = reviewCarePlan.map((ch) => {
          const match = checklistHeadings.find(
            (h) => h.id === ch.id || h.name === ch.heading?.name
          );
          return {
            ...ch,
            heading: {
              ...ch.heading,
              subheadings: ch.heading?.subheadings?.length
                ? ch.heading.subheadings
                : match?.subheadings || [],
            },
          };
        });
        setCarePlan(hydrated);
        initializedFromReview.current = true;
      } else if (!fromReview && activeChecklist?.headings) {
        // Default initialization for new care plans — a plan started from
        // scratch begins from the full baseline again, so drop any deletions
        // left over from a previous, abandoned flow.
        clearRemovedBaselineSubheadings(patientId);
        const careData = activeChecklist.headings.map((item) => {
          return {
            id: item?.id,
            heading: { name: item.name, subheadings: item.subheadings },
            functional_issues: [{ id: 1, intervention: [{}] }],
          };
        });

        setCarePlan(careData);
      }
    }
  }, [date, data, activeChecklist, fromReview, reviewCarePlan, carePlan]);
  useEffect(() => {
    if (isSuccess || updateSuccess) {
      queryClient.invalidateQueries({ queryKey: ["carePlanInitialScores", patientId] });
      queryClient.invalidateQueries({ queryKey: ["carePlanCurrentScores", patientId] });
      queryClient.invalidateQueries({ queryKey: ["carePlanMedianScores", patientId] });
      // The plan is saved, so its deletions are now part of the saved issues.
      clearRemovedBaselineSubheadings(patientId);
      setAllowNavigation(true); // Allow navigation after successful save
      toast.success(
        <p className="text-center">
          Your unfinished document <br /> will be saved in
          <br /> OUTSTANDING DOCUMENTS.
        </p>,
        {
          position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "bg_secondary",
          theme: "colored",
          transition: Bounce,
        },
      );

      // Navigate to outstanding documents page after successful save
      setTimeout(() => {
        props.history.push({
          pathname: "/hcp/outstanding_documents",
          state: { preventBrowserBack: true },
        });
      }, 1000); // Wait 1 second to allow user to see the toast
    }
  }, [isSuccess, updateSuccess, props.history]);

  // Track when data has been entered
  useEffect(() => {
    if (carePlan || type || meeting || attendees.attendees.length > 0 || date) {
      setHasDataBeenEntered(true);
      // Set session storage flag for logout warning
      sessionStorage.setItem("hasUnsavedCarePlan", "true");
    }
  }, [carePlan, type, meeting, attendees, date]);

  // Clear session storage flag when component unmounts or navigation is allowed
  useEffect(() => {
    return () => {
      // Clear the flag when component unmounts
      if (allowNavigation) {
        sessionStorage.removeItem("hasUnsavedCarePlan");
      }
    };
  }, [allowNavigation]);

  // Fallback: when baselines arrive after the user has already selected type/attendees
  useEffect(() => {
    if (!baselineScores || !carePlan) return;
    const isIndividual = type?.value === "INDIVIDUAL";
    const isCollaborativeReady =
      type?.value === "COLLABORATIVE" && attendees.confirmedAttendees;
    if (!isIndividual && !isCollaborativeReady) return;
    // For collaborative via nav bar (not from team scores), never auto-populate current score
    const skipCurrentScore = !fromTeamScores && type?.value === "COLLABORATIVE";
    // Subheadings the user deleted earlier in this flow (possibly on the review
    // page) must not be auto-added back.
    const removedSubheadingIds = getRemovedBaselineSubheadings(patientId);
    setCarePlan((prev) => {
      if (!prev || prev.length === 0) return prev;
      let changed = false;
      const updated = prev.map((careheading) => {
        // Update scores on already-selected issues
        const updatedIssues = careheading.functional_issues.map((issue) => {
          if (!issue.subheading?.id) return issue;
          const normalized = normalizeBaselineEntry(baselineScores[issue.subheading.id]);
          if (!normalized) return issue;
          const updates = {};
          if (normalized.initial_score != null && issue.initial_score !== normalized.initial_score) {
            updates.initial_score = normalized.initial_score;
            updates.initial_score_date = normalized.initial_score_date;
          }
          // The median substitutes for a score, it doesn't suppress one. On a
          // collaborative plan the real team scores must still land — the
          // median effect overwrites only the rows it has a number for, and
          // leaves the rest holding the team's own. On an individual plan the
          // median can deliberately blank a row, so baseline stays out of it.
          if (
            isFreshCarePlan &&
            !skipCurrentScore &&
            (!medianMode || isCollaborativePlan) &&
            normalized.current_score != null &&
            !issue.current_score
          ) {
            updates.current_score = normalized.current_score;
            updates.current_score_date = normalized.current_score_date;
          }
          if (Object.keys(updates).length > 0) {
            changed = true;
            return { ...issue, ...updates };
          }
          return issue;
        });
        // Auto-add rows for every baseline subheading on a fresh plan: the
        // team-scores flow, and a fresh Individual created from the navbar
        // (which pre-populates all subheadings with their initial + current
        // scores). Collaborative-from-navbar is excluded, so it stays blank.
        if ((fromTeamScores || isIndividual) && isFreshCarePlan) {
          const existingSubheadingIds = new Set(
            updatedIssues.filter((fi) => fi.subheading?.id).map((fi) => fi.subheading.id)
          );
          const subheadingsToAdd = (careheading.heading.subheadings || []).filter(
            (sub) =>
              baselineScores[sub.id] &&
              !existingSubheadingIds.has(sub.id) &&
              !removedSubheadingIds.has(sub.id)
          );
          if (subheadingsToAdd.length > 0) {
            changed = true;
            const maxId = updatedIssues.reduce(
              (max, fi) => Math.max(max, typeof fi.id === "number" ? fi.id : 0), 0
            );
            const newIssues = subheadingsToAdd.map((sub, idx) => {
              const normalized = normalizeBaselineEntry(baselineScores[sub.id]);
              return {
                id: maxId + idx + 1,
                subheading: sub,
                intervention: [{}],
                ...(normalized.initial_score != null ? { initial_score: normalized.initial_score } : {}),
                ...(normalized.initial_score_date ? { initial_score_date: normalized.initial_score_date } : {}),
                ...(normalized.current_score != null ? { current_score: normalized.current_score } : {}),
                ...(normalized.current_score_date ? { current_score_date: normalized.current_score_date } : {}),
              };
            });
            const nonEmptyIssues = updatedIssues.filter((fi) => fi.subheading?.id);
            return { ...careheading, functional_issues: [...nonEmptyIssues, ...newIssues] };
          }
        }
        return changed
          ? { ...careheading, functional_issues: updatedIssues }
          : careheading;
      });
      return changed ? updated : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineScores, type?.value, attendees.confirmedAttendees]);

  // Open first accordion when conditions for showing accordion are met
  useEffect(() => {
    // Check if accordion should be visible (matches the rendering condition)
    const shouldShowAccordion =
      date &&
      (type?.label === "Individual Provider Care Plan" ||
        type?.label === "Individual" ||
        (meeting && attendees.confirmedAttendees && attendees.attendees[0]));

    // If accordion is visible and carePlan exists, expand first item
    if (shouldShowAccordion && carePlan && carePlan.length > 0) {
      setExpandedAccordions(["accordion-0"]);
    }
  }, [date, type, meeting, attendees, carePlan]);

  // Handle browser close/refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasDataBeenEntered && !allowNavigation) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers show this message
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasDataBeenEntered, allowNavigation]);

  // Navigation blocking function
  const handleNavigationAttempt = (location) => {
    if (allowNavigation || !hasDataBeenEntered) {
      return true; // Allow navigation
    }

    // Allow navigation to review page without warning
    if (location.pathname === "/hcp/patient/care_review_plan") {
      return true;
    }

    // Don't show warning for dropdown anchor links (they just add # to URL)
    if (
      location.pathname === window.location.pathname &&
      location.hash === "#"
    ) {
      return true;
    }

    setPendingLocation(location);
    setShowNavigationModal(true);
    return false; // Block navigation
  };

  // Confirm navigation and lose data
  const handleConfirmNavigation = () => {
    setShowNavigationModal(false);

    // Navigate to the intended location immediately
    // Clear data in parallel with navigation
    if (pendingLocation) {
      // Clear all care plan data as we navigate
      setCarePlan(null);
      setType(null);
      setMeeting(null);
      setMeetingTypeId(null);
      setAttendees({
        confirmedAttendees: false,
        attendees: [],
      });
      setListOfAttendees(false);
      setDate(null);
      setChosenDate(null);
      localStorage.removeItem("type");

      // Set allowNavigation and navigate in the next tick
      // This ensures the Prompt component sees the updated state
      setAllowNavigation(true);
      setTimeout(() => {
        props.history.push(pendingLocation.pathname);
      }, 0);
    }
  };

  // Cancel navigation
  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
    setPendingLocation(null);
  };

  const handleFunction = (heading) => {
    const newIssue = carePlan.map((item) => {
      if (item?.id === heading) {
        // Draft/outstanding rows carry non-sequential backend ids, so derive the
        // next id from the existing max (not length) to guarantee uniqueness —
        // duplicate row ids break selection (rows are keyed by id).
        const nextId =
          item.functional_issues.reduce(
            (max, fi) => Math.max(max, typeof fi.id === "number" ? fi.id : 0),
            0
          ) + 1;
        return {
          ...item,
          functional_issues: [
            ...item.functional_issues,
            { id: nextId, intervention: [{}] },
          ],
        };
      }
      return item;
    });
    setCarePlan(newIssue);
    //setFunctionIssue([...functionIssue, { id: 1, heading, intervention: [1] }]);
  };

  // Extra warning for a plan that arrived with a type already decided —
  // switching costs more than the rows.
  const typeChangeCaveat = fromTeamScores
    ? "This plan was created from the team scores you just saved. Those scores will no longer be carried onto it."
    : fromReview
      ? "This plan already has details entered on the review page."
      : null;

  // Any issue actually chosen — the thing a type change throws away.
  const hasEnteredIssues = useMemo(
    () =>
      (carePlan || []).some((careheading) =>
        (careheading.functional_issues || []).some((fi) => fi.subheading?.id),
      ),
    [carePlan],
  );

  const applyTypeChange = (item) => {
    const isDifferent = item.value !== type?.value;
    setType(item);
    localStorage.setItem("type", item.value || item);
    // Do NOT call populateFromBaseline here; baselineScores is still the old
    // type's data. The fallback useEffect populates once the new query resolves.
    if (isDifferent) {
      setCarePlan((prev) => resetCarePlanRows(prev));
      // The rows are gone, so there is nothing left to restore — drop the
      // median without trying to put the old scores back.
      preMedianCurrentRef.current = null;
      medianFilledRowsRef.current = new Set();
      medianAddedRowIdsRef.current = new Set();
      medianRowsAddedRef.current = false;
      setMedianMode(false);
      setMedianPickerOpen(false);
      setPickedMedianRange(null);
    }
    if (item.value === "INDIVIDUAL") {
      setExpandedAccordions(["accordion-0"]);
    }
  };

  const handleType = (item) => {
    // A collaborative plan is built on team scores, and building one by hand
    // from the sidebar skips the step that produces them. Send the user to
    // Functional Summaries to assign them first — but not when the team-scores
    // flow already brought them here, and not when reopening a saved plan.
    if (
      item.value === "COLLABORATIVE" &&
      !fromTeamScores &&
      !fromReview &&
      isFreshCarePlan
    ) {
      setShowTeamScoreRedirectModal(true);
      return;
    }
    // Re-picking the same type changes nothing — don't warn about it.
    if (type?.value && item.value === type.value) return;
    // Switching type clears every functional issue on the plan, so confirm
    // first whenever there is something to lose.
    if (type?.value && (hasEnteredIssues || typeChangeCaveat)) {
      setPendingType(item);
      return;
    }
    applyTypeChange(item);
  };

  const handleConfirmTypeChange = () => {
    if (pendingType) applyTypeChange(pendingType);
    setPendingType(null);
  };
  const handleChooseIntervention = (
    interventionAction,
    intervIndex,
    heading,
    issueId,
  ) => {
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue.intervention[intervIndex] = interventionAction;
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleIntervention = (value, i, heading, issueId) => {
    const newIssue = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue.intervention.push({});
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(newIssue);
  };
  const handlePosition = (position, Intervention, heading, issueId) => {
    const matchingProviders = getProvidersForPosition(position);
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue.intervention[Intervention]["position"] = position;
              issue.intervention[Intervention]["provider"] =
                matchingProviders.length === 1 ? matchingProviders[0] : null;
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleProvider = (provider, intervention, heading, issueId) => {
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue.intervention[intervention]["provider"] = provider;
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleScore = (score, scoreType, heading, issueId) => {
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue[scoreType] = score;
              if (scoreType === "initial_score" && !(issue.subheading && baselineScores?.[issue.subheading.id])) {
                issue.current_score = score;
              }
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleAssignedDate = (date, dateType, heading, issueId) => {
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue[dateType] = date;
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleOutcome = (outcome, heading, issueId) => {
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue["outcome_measurements"] = outcome;
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleSetReviewDate = (date, intervention, heading, issueId) => {
    const interv = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((issue) => {
            if (issue?.id === issueId) {
              issue.intervention[intervention]["review_date"] = date;
            }
            return issue;
          }),
        };
      }
      return item;
    });
    setCarePlan(interv);
  };
  const handleIssue = (heading, issue, issueId) => {
    // Picking a previously deleted subheading again undoes the deletion.
    restoreRemovedBaselineSubheading(patientId, issue?.id);
    const skipCurrentScore =
      (!fromTeamScores && type?.value === "COLLABORATIVE") ||
      !isFreshCarePlan;
    const newIssue = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((funcIssue) => {
            if (funcIssue?.id === issueId) {
              const normalized = normalizeBaselineEntry(baselineScores?.[issue?.id]);
              const median = medianScores?.[issue?.id];
              return {
                ...funcIssue,
                subheading: issue,
                ...(normalized != null ? {
                  ...(normalized.initial_score != null ? { initial_score: normalized.initial_score } : {}),
                  ...(normalized.initial_score_date ? { initial_score_date: normalized.initial_score_date } : {}),
                  ...(!skipCurrentScore && normalized.current_score != null ? { current_score: normalized.current_score } : {}),
                  ...(!skipCurrentScore && normalized.current_score_date ? { current_score_date: normalized.current_score_date } : {}),
                } : {
                  initial_score_date: new Date(),
                  ...(!skipCurrentScore ? { current_score_date: new Date() } : {}),
                  ...(median != null ? { initial_score: median, ...(!skipCurrentScore ? { current_score: median } : {}) } : {}),
                }),
              };
            }
            return funcIssue;
          }),
        };
      }
      return item;
    });

    setCarePlan(newIssue);
  };

  const handleFunctionalIssueComment = (comment, heading, issueId) => {
    const updatedPlan = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((funcIssue) => {
            if (funcIssue?.id === issueId) {
              return { ...funcIssue, comment };
            }
            return funcIssue;
          }),
        };
      }
      return item;
    });
    setCarePlan(updatedPlan);
  };

  const handleInterventionComment = (
    comment,
    heading,
    issueId,
    interventionIndex,
  ) => {
    const updatedPlan = carePlan.map((item) => {
      if (item?.id === heading) {
        return {
          ...item,
          functional_issues: item.functional_issues.map((funcIssue) => {
            if (funcIssue?.id === issueId) {
              return {
                ...funcIssue,
                intervention: funcIssue.intervention.map((interv, index) => {
                  if (index === interventionIndex) {
                    return { ...interv, comment };
                  }
                  return interv;
                }),
              };
            }
            return funcIssue;
          }),
        };
      }
      return item;
    });
    setCarePlan(updatedPlan);
  };

  const handleClearOrRemoveFunctionalIssue = (headingId, issueId) => {
    // Remember baseline-backed deletions so neither this page nor the review
    // page auto-adds the row back on the next render.
    const deleted = carePlan
      .find((heading) => heading?.id === headingId)
      ?.functional_issues.find((issue) => issue?.id === issueId);
    if (deleted?.subheading?.id && baselineScores?.[deleted.subheading.id]) {
      addRemovedBaselineSubheading(patientId, deleted.subheading.id);
    }

    const updatedPlan = carePlan.map((heading) => {
      if (heading?.id !== headingId) {
        return heading;
      }

      if (heading.functional_issues.length === 1) {
        return {
          ...heading,
          functional_issues: heading.functional_issues.map((issue) => {
            if (issue?.id !== issueId) {
              return issue;
            }

            return {
              ...issue,
              subheading: undefined,
              initial_score: undefined,
              current_score: undefined,
              target_score: undefined,
              initial_score_date: undefined,
              current_score_date: undefined,
              comment: "",
              intervention: [{}],
            };
          }),
        };
      }

      return {
        ...heading,
        functional_issues: heading.functional_issues.filter(
          (issue) => issue?.id !== issueId,
        ),
      };
    });

    setCarePlan(updatedPlan);
  };

  const handleClearOrRemoveIntervention = (
    headingId,
    issueId,
    interventionIndex,
  ) => {
    const updatedPlan = carePlan.map((heading) => {
      if (heading?.id !== headingId) {
        return heading;
      }

      return {
        ...heading,
        functional_issues: heading.functional_issues.map((issue) => {
          if (issue?.id !== issueId) {
            return issue;
          }

          if (issue.intervention.length === 1) {
            return {
              ...issue,
              intervention: [{}],
            };
          }

          return {
            ...issue,
            intervention: issue.intervention.filter(
              (_, index) => index !== interventionIndex,
            ),
          };
        }),
      };
    });

    setCarePlan(updatedPlan);
  };

  const handleCollaborativeAttendees = (attendee) => {
    setAttendees((prevSelectedAttendees) => {
      if (prevSelectedAttendees.attendees.includes(attendee?.id)) {
        return {
          ...prevSelectedAttendees,
          attendees: prevSelectedAttendees.attendees.filter(
            (selectedAttendee) => selectedAttendee !== attendee?.id,
          ),
        };
      } else {
        return {
          ...prevSelectedAttendees,
          attendees: [...prevSelectedAttendees.attendees, attendee?.id],
        };
      }
    });
  };
  const handleSelectAllAttendees = () => {
    setAttendees((prevSelectedAttendees) => {
      if (allAvailableAttendeesSelected) {
        return {
          ...prevSelectedAttendees,
          attendees: prevSelectedAttendees.attendees.filter(
            (attendeeId) => !availableAttendeeIds.includes(attendeeId),
          ),
        };
      }

      return {
        ...prevSelectedAttendees,
        attendees: Array.from(
          new Set([...prevSelectedAttendees.attendees, ...availableAttendeeIds]),
        ),
      };
    });
  };
  const handleConfirmedAttendees = () => {
    setAttendees((prevState) => ({
      ...prevState,
      confirmedAttendees: true,
    }));
    setListOfAttendees(false);
    setExpandedAccordions(["accordion-0"]);
    if (baselineScores && fromTeamScores) {
      setCarePlan((prev) => populateFromBaseline(prev, baselineScores, false));
    }
  };
  const handleRemoveAttendees = () => {
    setAttendees({
      attendees: [],
      confirmedAttendees: false,
    });
  };
  // Handle footer button actions to allow navigation
  const handleClearAction = () => {
    // Show clear confirmation modal instead of clearing immediately
    setShowClearModal(true);
  };

  const openClearRowModal = (
    onConfirm,
    modalContent = {
      title: "Are you sure you want to clear this row?",
      note: "All scores and comments will be lost.",
    },
  ) => {
    setClearRowAction(() => onConfirm);
    setClearRowModalContent(modalContent);
    setShowClearRowModal(true);
  };

  const handleConfirmClearRow = () => {
    if (clearRowAction) {
      clearRowAction();
    }
    setShowClearRowModal(false);
    setClearRowAction(null);
    setClearRowModalContent({
      title: "Are you sure you want to clear this row?",
      note: "All scores and comments will be lost.",
    });
  };

  const handleCancelClearRow = () => {
    setShowClearRowModal(false);
    setClearRowAction(null);
    setClearRowModalContent({
      title: "Are you sure you want to clear this row?",
      note: "All scores and comments will be lost.",
    });
  };

  // Handle clear confirmation
  const handleConfirmClear = () => {
    setShowClearModal(false);

    // Clear all care plan data but KEEP the date
    setCarePlan(null);

    // Keep date and chosenDate - don't clear them
    localStorage.removeItem("type");

    // Clear session storage flag since data is cleared
    sessionStorage.removeItem("hasUnsavedCarePlan");
    setHasDataBeenEntered(false);

    // Stay on the same page - no navigation needed
  };

  // Handle cancel clear
  const handleCancelClear = () => {
    setShowClearModal(false);
  };

  const handleSaveForLaterAction = () => {
    setAllowNavigation(true);
    // Clear session storage flag when saving for later
    sessionStorage.removeItem("hasUnsavedCarePlan");
  };

  const handleReviewAction = () => {
    setAllowNavigation(true);
    // Clear session storage flag when going to review
    sessionStorage.removeItem("hasUnsavedCarePlan");
  };

  const handleSubmitCareplan = async () => {
    // When amending a signed plan or editing any version > 1, a non-blank
    // reason is required.
    if (requiresEditReason && !editReason.trim()) {
      toast.warn("Please provide a reason for creating another version", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    const authorId = JSON.parse(localStorage.getItem("user")).organizations[0]
      .careProvider?.id;

    const dataToSubmit = carePlan
      .flatMap((item) => item.functional_issues)
      .map((funcIssue, issueIndex) => {
        const validInterventions = (funcIssue.intervention || [])
          .filter(
            (intervention) =>
              intervention?.id ||
              intervention?.provider?.id ||
              intervention?.position?.id ||
              intervention?.review_date
          )
          .map((intervention, interventionIndex) => ({
            labelId: intervention.id || null,
            assigneeId: intervention.provider?.id || null,
            assigneePositionId: intervention.position?.id || null,
            reviewDate: intervention.review_date || null,
            comment: intervention.comment || "",
            orderIndex: interventionIndex,
          }));

        const subheadingId = funcIssue.subheading?.id || null;
        const currentScore = funcIssue.current_score || null;
        const assignedDate = funcIssue.current_score_date || null;

        return {
          currentScore,
          targetScore: funcIssue.target_score || null,
          assignedDate,
          subheadingId,
          comment: funcIssue.comment || "",
          orderIndex: issueIndex,
          interventions: validInterventions,
        };
      });

    const getLocalISOString = () => {
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      const sign = offset >= 0 ? "+" : "-";
      const absOffset = Math.abs(offset);
      const hh = String(Math.floor(absOffset / 60)).padStart(2, "0");
      const mm = String(absOffset % 60).padStart(2, "0");
      const pad = (n) => String(n).padStart(2, "0");
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}${sign}${hh}:${mm}`;
    };

    let payload;
    if (type.value === "INDIVIDUAL") {
      payload = {
        entryDate: date,
        dateCreated: getLocalISOString(),
        patientId: parseInt(patientId),
        authorId,
        type: type.value,
        status: "DRAFT",
        attendeeIds: [authorId],
        issues: dataToSubmit.length > 0 ? dataToSubmit : [],
        // Only sent when a reason is required (editing a version > 1). The
        // backend stores it only on amended versions and ignores a blank value.
        ...(requiresEditReason &&
          editReason.trim() && { editReason: editReason.trim() }),
      };
    } else {
      // The author is never pickable in the attendee list, so add them here.
      // Building a new array (rather than pushing into state) keeps a second
      // save from sending them twice.
      const attendeeIds = attendees.attendees.includes(authorId)
        ? [...attendees.attendees]
        : [...attendees.attendees, authorId];
      payload = {
        entryDate: date,
        dateCreated: getLocalISOString(),
        patientId: parseInt(patientId),
        authorId,
        type: type.value,
        status: "DRAFT",
        issues: dataToSubmit.length > 0 ? dataToSubmit : [],
        meetingTypeId,
        attendeeIds,
        ...(teamScoreBatchId ? { teamScoreBatchId } : {}),
        // Only sent when a reason is required (editing a version > 1). The
        // backend stores it only on amended versions and ignores a blank value.
        ...(requiresEditReason &&
          editReason.trim() && { editReason: editReason.trim() }),
      };
    }

    if (isAmendMode && reviewCarePlanId) {
      // SIGNED plans can't be edited in place — amend to create a new draft
      // version, then save the user's changes onto that new version.
      amendMutate(
        { carePlanId: reviewCarePlanId, editReason: editReason.trim() },
        {
          onSuccess: (res) => {
            const newId = res?.data?.id;
            if (!newId) {
              toast.error("Could not create a new version. Please try again.");
              return;
            }
            updateMutate({ carePlanId: newId, carePlan: payload });
          },
          onError: () => {
            toast.error("Could not create a new version. Please try again.");
          },
        },
      );
      return;
    }

    const targetCarePlanId = reviewCarePlanId || outstandingCarePlanId;
    if (targetCarePlanId) {
      updateMutate({ carePlanId: targetCarePlanId, carePlan: payload });
    } else {
      mutate(payload);
    }
  };
  return (
    <>
      {hasDataBeenEntered && (
        <Prompt when={!allowNavigation} message={handleNavigationAttempt} />
      )}
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100 verticalScroll">
          <div className="card-body mx-0">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="font-weight-lighter">HCP: CREATE Care Plan</h5>
              <div className="d-flex align-items-center">
                <div className="d-flex align-items-center mr-4">
                  {date && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowLatestScoresOverlay(true)}
                      aria-haspopup="dialog"
                      aria-expanded={showLatestScoresOverlay}
                    >
                      <small>Functional Summaries</small>
                    </button>
                  )}
                </div>
                {date && (
                  <div className="media align-items-center">
                    <h6>
                      <small>
                        Meeting/Entry Date:{" "}
                        <sup>
                          <FaInfoCircle />
                        </sup>
                      </small>
                    </h6>
                    <div className="media-body ml-2" style={{ width: "160px" }}>
                      <PickDate
                        date={date}
                        setDate={setDate}
                        maxDate={new Date()}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />
            {/* Reason for creating another version — required when amending a
                signed care plan into a new version */}
            {requiresEditReason && date && (
              <div className="mb-3">
                <small className="text-danger">*</small>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: "60%" }}
                    placeholder="mandatory - complete reason for creating another version"
                    maxLength={1000}
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn p-0 reason-submit-btn ml-2 d-flex align-items-center justify-content-center"
                    title="Submit reason"
                    disabled={!editReason.trim()}
                    onClick={() =>
                      editReason.trim() && setEditReasonConfirmed(true)
                    }
                    style={{
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      borderRadius: 20,
                    }}
                  >
                    <FiArrowRight size={32} color="#fff" strokeWidth={3} />
                  </button>
                </div>
              </div>
            )}
            {!isLoading && !error && date && showCarePlanBody && (
              <div className="row">
                <div className="col-md-4 pt_1">
                  <div className="dropdown">
                    <button
                      className="btn py-2 btn-block border text-left"
                      type="button"
                      id="dropdownMenuButton"
                      data-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <small>
                          {type ? type?.label : "Choose Type of Care Plan"}
                        </small>
                        <BiChevronDown size={30} className="navbar-btn-icon" />
                      </div>
                    </button>
                    <div
                      className="dropdown-menu custom border-0 shadow"
                      aria-labelledby="dropdownMenuButton"
                    >
                      <p className="pl-4 mb-0 pb-2">
                        <small>Choose one type only:</small>
                      </p>
                      {careTypes.map((item, key) => (
                        <label
                          key={key}
                          className="dropdown-item ml-4"
                          onClick={() => handleType(item)}
                        >
                          <small>{item.label}</small>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Individual plans pick their own period for the median. */}
                {/* Which numbers fill Current Score: the provider's own, or
                    the median over a period they choose. */}
                {isIndividualPlan && (
                  <div className="col-md-4 pt_1">
                    <div className="dropdown">
                      <div
                        className="score-source-toggle"
                        role="group"
                        aria-label="Current score source"
                      >
                        <button
                          type="button"
                          className={`score-source-toggle-option ${
                            !medianMode ? "is-active" : ""
                          }`}
                          aria-pressed={!medianMode}
                          title="Fill Current Score with your own latest scores"
                          onClick={() => {
                            setMedianPickerOpen(false);
                            if (medianMode) handleClearMedian();
                          }}
                        >
                          My Scores
                          {/* Nested in the option, so its click has to be kept
                              from reaching the button and switching source. A
                              span rather than a button: buttons cannot nest. */}
                          <span
                            role="button"
                            tabIndex={0}
                            className="score-source-info"
                            title="What My Scores means"
                            aria-label="What My Scores means"
                            aria-expanded={scoreSourceInfoOpen}
                            onClick={handleToggleScoreSourceInfo}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter" && event.key !== " ")
                                return;
                              handleToggleScoreSourceInfo(event);
                            }}
                          >
                            <FaInfoCircle size={12} />
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`score-source-toggle-option ${
                            medianMode ? "is-active" : ""
                          }`}
                          aria-pressed={medianMode}
                          aria-expanded={medianPickerOpen}
                          title="Fill Current Score with the median across all providers"
                          onClick={() =>
                            medianPickerOpen
                              ? setMedianPickerOpen(false)
                              : handleOpenMedianPicker()
                          }
                        >
                          Median
                          <BsChevronDown className="ml-1" style={{ fontSize: "9px" }} />
                        </button>
                      </div>
                      {scoreSourceInfoOpen && (
                        <div className="score-source-info-popover" role="note">
                          <b>My Scores</b> are the last assessment scores of the
                          account owner.
                        </div>
                      )}
                      {medianMode && medianRange && (
                        <small className="score-source-toggle-caption">
                          {moment(medianRange.from).format("D MMM")} –{" "}
                          {moment(medianRange.to).format("D MMM YYYY")}
                        </small>
                      )}
                      <div
                        className={`dropdown-menu custom border-0 shadow px-3 pb-3 ${
                          medianPickerOpen ? "show" : ""
                        }`}
                        style={{ minWidth: "100%" }}
                      >
                        <p className="mb-2 pt-2">
                          <small>
                            Fill Current Score with the median across all
                            providers for:
                          </small>
                        </p>
                        <div className="row mb-3">
                          <div className="col-6">
                            <small className="text-muted">From</small>
                            <PickDate
                              date={draftMedianRange.from}
                              setDate={(value) =>
                                setDraftMedianRange((prev) => ({
                                  ...prev,
                                  from: value,
                                }))
                              }
                              required={false}
                              maxDate={new Date()}
                            />
                          </div>
                          <div className="col-6">
                            <small className="text-muted">To</small>
                            <PickDate
                              date={draftMedianRange.to}
                              setDate={(value) =>
                                setDraftMedianRange((prev) => ({
                                  ...prev,
                                  to: value,
                                }))
                              }
                              required={false}
                              maxDate={new Date()}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary btn-block"
                          onClick={handleApplyMedian}
                        >
                          <small>
                            {medianMode ? "Update Median" : "Apply Median"}
                          </small>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {type?.label == "Collaborative Care Plan" && (
                  <div className="col-md-4">
                    <div className="dropdown">
                      <button
                        className="btn py-2 btn-block border text-left"
                        type="button"
                        id="dropdownMenuButton"
                        data-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <small>
                            {meeting ? meeting : "Choose Type of Meeting"}
                          </small>
                          <BiChevronDown
                            size={30}
                            className="navbar-btn-icon"
                          />
                        </div>
                      </button>
                      <div
                        className="dropdown-menu custom border-0 shadow pb-4"
                        aria-labelledby="dropdownMenuButton"
                      >
                        <p className="pl-4 mb-0 pb-2">
                          <small>Choose one type only:</small>
                        </p>
                        {meetingTypesData?.data?.map((item, key) => (
                          <label
                            key={key}
                            className="dropdown-item ml-4"
                            onClick={() => {
                              setMeeting(item.name);
                              setMeetingTypeId(item.id);
                            }}
                          >
                            <small>{item.name}</small>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {type?.label == "Collaborative Care Plan" && meeting && (
                  <div className="col-md-3">
                    <div className="dropdown">
                      <button
                        className="btn py-2 btn-block border text-left"
                        type="button"
                        // id="dropdownMenuButton"
                        // data-toggle="dropdown"
                        aria-expanded="false"
                        onClick={() => setListOfAttendees(!listOfAttendees)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <small>
                            Confirm Attendees: {attendees.attendees.length}
                          </small>
                          <BiChevronDown
                            size={30}
                            className="navbar-btn-icon"
                          />
                        </div>
                      </button>
                      <div
                        className={`dropdown-menu custom border-0 shadow ${
                          listOfAttendees ? "show" : ""
                        }`}
                        // aria-labelledby="dropdownMenuButton"
                      >
                        <p className="pl-4 mb-0 pb-2">
                          <small>Choose as many as applicable:</small>
                        </p>
                        <div className="form-group form-check my-3 p-0 ml-5">
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            checked={allAvailableAttendeesSelected}
                            disabled={availableAttendeeIds.length === 0}
                            onChange={handleSelectAllAttendees}
                          />
                          &nbsp;&nbsp;&nbsp;
                          <label className="form-check-label">
                            <small>Select All</small>
                          </label>
                        </div>
                        {attendeeOptions.map((item, key) => (
                          <div
                            key={key}
                            className=" form-group form-check my-3 p-0 ml-5"
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-2"
                              checked={attendees.attendees.includes(item?.id)}
                              onChange={() =>
                                handleCollaborativeAttendees(item)
                              }
                            />
                            &nbsp;&nbsp;&nbsp;
                            <label
                              className="form-check-label"
                              for="exampleCheck1"
                            >
                              <small>
                              {item.position?.abbreviation}:{" "}
                                {item.lastName}, {item.firstName}
                              </small>
                            </label>
                          </div>
                        ))}
                        {attendeeOptions.length === 0 && (
                          <p className="pl-4 mb-0 pb-2 text-muted">
                            <small>
                              No other care providers are assigned to this
                              patient
                            </small>
                          </p>
                        )}
                        <div className="d-flex justify-content-between align-items-center mx-3 mb-2 mt-5">
                          <button
                            className="btn btn_outline_danger px-4"
                            onClick={() => handleRemoveAttendees()}
                          >
                            <small className="mx-3">Clear</small>
                          </button>
                          <button
                            className="btn btn-primary px-4"
                            onClick={() => handleConfirmedAttendees()}
                          >
                            <small className="mx-3">Enter</small>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* The medians stand in for the team scores only when that was
                    chosen back on Functional Summaries, so there is nothing to
                    switch here — just a note of the period they cover. */}
                {/* {isCollaborativePlan && medianMode && (
                  <div className="col-md-4 pt_1">
                    <small className="score-source-note">
                      Medians used as team scores ·{" "}
                      {teamScoreRange.start.format("D MMM")} –{" "}
                      {teamScoreRange.end.format("D MMM YYYY")}
                    </small>
                  </div>
                )} */}
              </div>
            )}
            {isLoading && (
              <div className="text-center py-5">
                <p>Loading care plan data...</p>
              </div>
            )}
            {error && (
              <div className="text-center py-5 text-danger">
                <p>Error loading care plan data. Please try again.</p>
              </div>
            )}
            {!isLoading &&
              !error &&
              date &&
              (type?.label == "Individual Provider Care Plan" ||
                type?.label == "Individual" ||
                (meeting &&
                  attendees.confirmedAttendees &&
                  attendees.attendees[0])) &&
              showCarePlanBody && (
                <div className="py_2">
                  <Accordion
                    allowZeroExpanded
                    allowMultipleExpanded
                    className="mb-4 py-0"
                    preExpanded={expandedAccordions}
                    onChange={setExpandedAccordions}
                  >
                    {!isLoading &&
                      data?.data &&
                      carePlan &&
                      carePlan.map((careheading, i) => (
                        <div key={i}>
                          <div className="chevron" />
                          <AccordionItem
                            className="mb-2"
                            uuid={`accordion-${i}`}
                          >
                            <AccordionItemHeading className="accordion__primary">
                              <AccordionItemButton
                                style={{
                                  height: 45,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <small>{careheading.heading.name}</small>
                              </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                              <div
                                className="row text-center text-white head_light mx-0"
                                style={{ minHeight: "35px" }}
                              >
                                <div className="col-md-3 border-1 d-flex justify-content-center align-items-center">
                                  <small className="f-11">
                                    Functional Issue
                                  </small>
                                </div>
                                <div className="col border-1 px-4 d-flex justify-content-center align-items-center" style={{ order: 3 }}>
                                  <small className="f-11">
                                    {type?.value === "COLLABORATIVE" ? "Initial Team Score" : "Initial Ind Score"}
                                  </small>
                                </div>
                                <div className="col border-1 text-center px-4 d-flex justify-content-center align-items-center" style={{ order: 4 }}>
                                  <small className="f-11">Assigned Date</small>
                                </div>
                                <div className="col border-1 text-center px-4 d-flex justify-content-center align-items-center" style={{ order: 1 }}>
                                  <small className="f-11">{currentScoreColumnLabel}</small>
                                </div>
                                <div className="col border-1 text-center px-4 d-flex justify-content-center align-items-center" style={{ order: 2 }}>
                                  <small className="f-11">Assigned Date</small>
                                </div>
                                <div className="col border-1 text-center px-4 d-flex justify-content-center align-items-center" style={{ order: 5 }}>
                                  <small className="f-11">{type?.value === "COLLABORATIVE" ? "Target Team Score" : "Target Ind Score"}</small>
                                </div>
                                {/* <div className="col-md-2 border-1 d-none">
                                  <small className="d-flex justify-content-center align-items-center h-100 f-11">
                                    Outcome Measurements
                                  </small>
                                </div> */}
                                <div className="col-md-3 border-1 d-flex justify-content-center align-items-center" style={{ order: 6 }}>
                                  <small className="f-11">Actions</small>
                                </div>
                              </div>
                              {[...careheading.functional_issues]
                                .sort((a, b) => {
                                  const sa = a.current_score, sb = b.current_score;
                                  if (sa == null || sa === 'N/A') return 1;
                                  if (sb == null || sb === 'N/A') return -1;
                                  return sa - sb;
                                })
                                .map((item) => {

                                return(
                                <div key={item?.id}>
                                  <div className="row text-center mx-0 mt-1">
                                    <div className="col-md-3 px-0">
                                      <div className="dropdown">
                                        <button
                                          className="btn btn-block border"
                                          type="button"
                                          id="dropdownMenuButton"
                                          data-toggle="dropdown"
                                          aria-expanded="false"
                                          style={{
                                            height: "37.3px",
                                            padding: "0 12px",
                                          }}
                                        >
                                          <div className="d-flex align-items-center justify-content-between">
                                            <small className="f-11">
                                              {item.subheading
                                                ? item.subheading.name
                                                : "Select Issue"}
                                            </small>
                                            <BsChevronDown />
                                          </div>
                                        </button>
                                        <div
                                          className="dropdown-menu custom border-0 shadow"
                                          aria-labelledby="dropdownMenuButton"
                                        >
                                          <p className="pl-4 mb-0 pb-2">
                                            <small
                                              className="small-text"
                                              style={{ fontFamily: 700 }}
                                            >
                                              Choose one issue only:
                                            </small>
                                          </p>
                                          {careheading.heading.subheadings.map(
                                            (subheading, key) => (
                                              <a
                                                className="dropdown-item ml-4"
                                                href="#"
                                                key={key}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleIssue(
                                                    careheading?.id,
                                                    subheading,
                                                    item?.id,
                                                  );
                                                }}
                                              >
                                                <small className="small-text">
                                                  {subheading.name}
                                                </small>
                                              </a>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col px-1" style={{ order: 3 }}>
                                      <div className="dropdown">
                                        <button
                                          className={`btn btn-block border px-1 bg-white ${
                                            item.initial_score &&
                                            "bg-transparent border-0"
                                          }`}
                                          type="button"
                                          id="dropdownMenuButton"
                                          data-toggle={item.subheading && baselineScores?.[item.subheading.id] ? undefined : "dropdown"}
                                          disabled={!!(item.subheading && baselineScores?.[item.subheading.id])}
                                          title={item.subheading && baselineScores?.[item.subheading.id] ? "Initial score is fixed for this admission" : undefined}
                                          aria-expanded="false"
                                          style={{
                                            borderRadius: "4px",
                                            height: "37.3px",
                                            padding: "0 12px",
                                            cursor: item.subheading && baselineScores?.[item.subheading.id] ? "default" : "pointer",
                                            opacity: 1,
                                          }}
                                        >
                                          <div className="d-flex align-items-center justify-content-center">
                                            {item && item.initial_score ? (
                                              <div
                                                className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), item.initial_score).className}`}
                                                style={{
                                                  width: "28px",
                                                  height: "28px",
                                                  fontSize: "12px",
                                                  minWidth: "28px",
                                                 ...getScoreOptionPillProps(resolveScale(item.subheading?.id), item.initial_score).style,}}
                                              >
                                                {item.initial_score}
                                              </div>
                                            ) : (
                                              <small className="f-11">
                                                Select
                                              </small>
                                            )}
                                            {!(item.subheading && baselineScores?.[item.subheading.id]) && (
                                              <BsChevronDown
                                                className="text-secondary"
                                                style={{
                                                  fontSize: "12px",
                                                  position: "absolute",
                                                  right: "8px",
                                                }}
                                              />
                                            )}
                                          </div>
                                        </button>
                                        <div
                                          className="dropdown-menu small border-0 shadow"
                                          aria-labelledby="dropdownMenuButton"
                                        >
                                          <p className="pl-4 mb-0 pb-2">
                                            <small>
                                              Choose one score only:
                                            </small>
                                          </p>
                                          {getScore(resolveScale(item.subheading?.id))
                                            .sort((a, b) => {
                                              // Put N/A last, then sort numbers in descending order
                                              if (a.value === "N/A") return 1;
                                              if (b.value === "N/A") return -1;
                                              return b.value - a.value;
                                            })
                                            .map((score, key) => (
                                              <a
                                                className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                  item.initial_score ===
                                                  score.value
                                                    ? "bg-light"
                                                    : ""
                                                }`}
                                                key={key}
                                                href="#"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleScore(
                                                    score.value,
                                                    "initial_score",
                                                    careheading?.id,
                                                    item?.id,
                                                  );
                                                }}
                                              >
                                                <small
                                                  className={`w-27 h-27 rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").className}`}
                                                style={getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").style}
                                                >
                                                  {score.value}
                                                </small>
                                              </a>
                                            ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col px-1" style={{ order: 4 }}>
                                      <div style={{ height: "37.3px" }}>
                                        <InputDatePicker
                                          date={
                                            item && item.initial_score_date
                                              ? item.initial_score_date
                                              : ""
                                          }
                                          setDate={handleAssignedDate}
                                          dateType={"initial_score_date"}
                                          heading={careheading?.id}
                                          issueId={item?.id}
                                          intervention={null}
                                          height="37.3px"
                                          style={{ width: "100%" }}
                                          noOffset
                                          maxDate={new Date()}
                                          disabled={!!(item.subheading && baselineScores?.[item.subheading.id])}
                                        />
                                      </div>
                                    </div>
                                    <div className="col px-1" style={{ order: 1 }}>
                                      <div className="dropdown">
                                        <button
                                          className={`btn btn-block border px-1 bg-white ${
                                            item.current_score &&
                                            "bg-transparent border-0"
                                          }`}
                                          type="button"
                                          id="dropdownMenuButton"
                                          data-toggle={isCurrentScoreLocked(item) ? undefined : "dropdown"}
                                          aria-expanded="false"
                                          disabled={isCurrentScoreLocked(item)}
                                          title={currentScoreTitle(item)}
                                          style={{
                                            borderRadius: "4px",
                                            height: "37.3px",
                                            padding: "0 12px",
                                            cursor: isCurrentScoreLocked(item) ? "default" : "pointer",
                                          }}
                                        >
                                          <div className="d-flex align-items-center justify-content-center">
                                            {item && item.current_score ? (
                                              <div
                                                className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), item.current_score).className}`}
                                                style={{
                                                  width: "28px",
                                                  height: "28px",
                                                  fontSize: "12px",
                                                  minWidth: "28px",
                                                 ...getScoreOptionPillProps(resolveScale(item.subheading?.id), item.current_score).style,}}
                                              >
                                                {item.current_score}
                                              </div>
                                            ) : (
                                              <small className="f-11">
                                                Select
                                              </small>
                                            )}
                                            {!isCurrentScoreLocked(item) && (
                                              <BsChevronDown
                                                className="text-secondary"
                                                style={{
                                                  fontSize: "12px",
                                                  position: "absolute",
                                                  right: "8px",
                                                }}
                                              />
                                            )}
                                          </div>
                                        </button>
                                        <div
                                          className="dropdown-menu small border-0 shadow"
                                          aria-labelledby="dropdownMenuButton"
                                        >
                                          <p className="pl-4 mb-0 pb-2">
                                            <small>
                                              Choose one score only:
                                            </small>
                                          </p>
                                          {getScore(resolveScale(item.subheading?.id))
                                            .sort((a, b) => {
                                              if (a.value === "N/A") return 1;
                                              if (b.value === "N/A") return -1;
                                              return b.value - a.value;
                                            })
                                            .map((score, key) => (
                                              <a
                                                className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                  item.current_score ===
                                                  score.value
                                                    ? "bg-light"
                                                    : ""
                                                }`}
                                                key={key}
                                                href="#"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleScore(
                                                    score.value,
                                                    "current_score",
                                                    careheading?.id,
                                                    item?.id,
                                                  );
                                                }}
                                              >
                                                <small
                                                  className={`w-27 h-27 rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").className}`}
                                                style={getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").style}
                                                >
                                                  {score.value}
                                                </small>
                                              </a>
                                            ))}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col px-1" style={{ order: 2 }}>
                                      <div style={{ height: "37.3px" }}>
                                        <InputDatePicker
                                          date={
                                            item && item.current_score_date
                                              ? item.current_score_date
                                              : ""
                                          }
                                          setDate={handleAssignedDate}
                                          dateType={"current_score_date"}
                                          heading={careheading?.id}
                                          issueId={item?.id}
                                          intervention={null}
                                          height="37.3px"
                                          style={{ width: "100%" }}
                                          noOffset
                                          maxDate={new Date()}
                                          disabled={isCurrentScoreLocked(item)}
                                        />
                                      </div>
                                    </div>
                                    <div className="col px-1" style={{ order: 5 }}>
                                      <div className="dropdown">
                                        <button
                                          className={`btn btn-block border px-1 bg-white ${
                                            item.target_score &&
                                            "bg-transparent border-0"
                                          }`}
                                          type="button"
                                          id="dropdownMenuButton"
                                          data-toggle="dropdown"
                                          aria-expanded="false"
                                          style={{
                                            borderRadius: "4px",
                                            height: "37.3px",
                                            padding: "0 12px",
                                          }}
                                        >
                                          <div className="d-flex align-items-center justify-content-center">
                                            {item && item.target_score ? (
                                              <div
                                                className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), item.target_score).className}`}
                                                style={{
                                                  width: "28px",
                                                  height: "28px",
                                                  fontSize: "12px",
                                                  minWidth: "28px",
                                                 ...getScoreOptionPillProps(resolveScale(item.subheading?.id), item.target_score).style,}}
                                              >
                                                {item.target_score}
                                              </div>
                                            ) : (
                                              <small className="f-11">
                                                Select
                                              </small>
                                            )}
                                            <BsChevronDown
                                              className="text-secondary"
                                              style={{
                                                fontSize: "12px",
                                                position: "absolute",
                                                right: "8px",
                                              }}
                                            />
                                          </div>
                                        </button>
                                        <div
                                          className="dropdown-menu small border-0 shadow fit"
                                          aria-labelledby="dropdownMenuButton"
                                        >
                                          <p className="pl-4 mb-0 pb-2">
                                            <small>
                                              Choose one score only:
                                            </small>
                                          </p>
                                          {getScore(resolveScale(item.subheading?.id))
                                            .sort((a, b) => {
                                              // Put N/A last, then sort numbers in descending order
                                              if (a.value === "N/A") return 1;
                                              if (b.value === "N/A") return -1;
                                              return b.value - a.value;
                                            })
                                            .map((score, key) => (
                                              <a
                                                className={`dropdown-item ml-4 border_primary mr-2 mb-1 rounded-5 h-35 ${
                                                  item.target_score ===
                                                  score.value
                                                    ? "bg-light"
                                                    : ""
                                                }`}
                                                key={key}
                                                href="#"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  handleScore(
                                                    score.value,
                                                    "target_score",
                                                    careheading?.id,
                                                    item?.id,
                                                  );
                                                }}
                                              >
                                                <small
                                                  className={`w-27 h-27 rounded-circle d-flex align-items-center justify-content-center font-weight-bold ${getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").className}`}
                                                style={getScoreOptionPillProps(resolveScale(item.subheading?.id), score.value, "head_light f-10").style}
                                                >
                                                  {score.value}
                                                </small>
                                              </a>
                                            ))}
                                        </div>
                                      </div>
                                    </div>
                                    {/* <div className="col-md-2 p-0 my-0">
                                      <input
                                        type="text"
                                        placeholder=""
                                        value={item.outcome_measurements}
                                        className="form-control border py-1 f-11 outline-none"
                                        onChange={(e) =>
                                          handleOutcome(
                                            e.target.value,
                                            careheading?.id,
                                            item?.id
                                          )
                                        }
                                      />
                                    </div> */}
                                    <div className="col-md-3 px-0 pl-2" style={{ order: 6 }}>
                                      <div className="d-flex align-items-center">
                                        <input
                                          type="text"
                                          placeholder="Comments"
                                          value={item.comment || ""}
                                          onChange={(e) =>
                                            handleFunctionalIssueComment(
                                              e.target.value,
                                              careheading?.id,
                                              item?.id,
                                            )
                                          }
                                          className="form-control py-4 border f-11 text-center outline-none"
                                          style={{
                                            height: "37.3px",
                                            padding: "0 12px",
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="btn p-0 ml-2 d-flex align-items-center justify-content-center"
                                          title="Clear or remove functional issue"
                                          onClick={() =>
                                            openClearRowModal(
                                              () =>
                                                handleClearOrRemoveFunctionalIssue(
                                                  careheading?.id,
                                                  item?.id,
                                                ),
                                              {
                                                title:
                                                  "Are you sure you want to delete this Functional Issue?",
                                                note: "The Functional Issue along with all Interventions will be lost.",
                                              },
                                            )
                                          }
                                        >
                                          <FiTrash2
                                            className="text_danger"
                                            size={ICON_SIZE}
                                          />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  {item.intervention.map(
                                    (intervention, Index) => {
                                      const rowProviders = getProvidersForPosition(
                                        intervention?.position,
                                      );

                                      return (
                                      <div
                                        key={`${item?.id || i}-intervention-${Index}`}
                                        className="row text-center mx-0 align-items-center"
                                      >
                                        <div className="col-md-3 pl-5 pr-0">
                                          <div className="dropdown">
                                            <button
                                              className="btn btn-block border px-1"
                                              type="button"
                                              id="dropdownMenuButton"
                                              data-toggle="dropdown"
                                              aria-expanded="false"
                                              style={{
                                                height: "37.3px",
                                                padding: "0 12px",
                                              }}
                                            >
                                              <div className="d-flex align-items-center justify-content-between">
                                                <small className="f-11">
                                                  {intervention &&
                                                  intervention.name
                                                    ? intervention.name
                                                    : "Select Intervention"}
                                                </small>
                                                <BsChevronDown />
                                              </div>
                                            </button>
                                            <div
                                              className="dropdown-menu small border-0 shadow unfit"
                                              aria-labelledby="dropdownMenuButton"
                                            >
                                              <p className="pl-4 mb-0 pb-2">
                                                <small className="small-text">
                                                  Choose one intervention only:
                                                </small>
                                              </p>

                                              {interventions?.data &&
                                                Object.keys(
                                                  interventions.data,
                                                ).map((interv, key) => (
                                                  <div key={key}>
                                                    <p className="pl-4 mb-0 pb-2">
                                                      <small className="small-text">
                                                        {interv}:
                                                      </small>
                                                    </p>
                                                    {interventions.data[
                                                      interv
                                                    ].map((action, i) => (
                                                      <a
                                                        key={i}
                                                        className="dropdown-item ml-4 mr-5"
                                                        href="#"
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          handleChooseIntervention(
                                                            {
                                                              id: action?.id,
                                                              name: action.name,
                                                            },
                                                            Index,
                                                            careheading?.id,
                                                            item?.id,
                                                          );
                                                        }}
                                                      >
                                                        <small className="small-text">
                                                          {action.name}
                                                        </small>
                                                      </a>
                                                    ))}
                                                  </div>
                                                ))}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-1 border-1 text-right">
                                          <small className="f-8">
                                            Assigned <br /> to:
                                          </small>
                                        </div>
                                        <div className="col-md-2 px-1">
                                          <div className="dropdown">
                                            <button
                                              className="btn btn-block border px-1"
                                              type="button"
                                              id="dropdownMenuButton"
                                              data-toggle="dropdown"
                                              aria-expanded="false"
                                              style={{
                                                height: "37.3px",
                                                padding: "0 12px",
                                              }}
                                            >
                                              <div className="d-flex align-items-center justify-content-between">
                                                <small className="f-11">
                                                  {intervention &&
                                                  intervention.position &&
                                                  intervention.position.name
                                                    ? intervention.position
                                                        .abbreviation
                                                    : " Select Position"}
                                                </small>
                                                <BsChevronDown />
                                              </div>
                                            </button>
                                            <div
                                              className="dropdown-menu small border-0 shadow unfit"
                                              aria-labelledby="dropdownMenuButton"
                                            >
                                              <p className="pl-4 mb-0 pb-2">
                                                <small>
                                                  Choose one position only:
                                                </small>
                                              </p>
                                              {/* remember to update the position as some position have subPositions */}
                                              {positions?.data?.content
                                                ?.sort((a, b) =>
                                                  a.position.name.localeCompare(
                                                    b.position.name,
                                                  ),
                                                )
                                                ?.flatMap((position, key) => {
                                                  if (
                                                    position.position.name ===
                                                      "Physician" &&
                                                    position.position.name
                                                  ) {
                                                    return [
                                                      <a
                                                        key={i}
                                                        className="ml-4 pl-1 py-3 decoration-none"
                                                        href="#"
                                                        onClick={(e) =>
                                                          e.preventDefault()
                                                        }
                                                      >
                                                        <small className="small-text font-weight-bold">
                                                          {
                                                            position.position
                                                              .name
                                                          }
                                                          :
                                                        </small>
                                                      </a>,
                                                      ...position.position.subPositions.map(
                                                        (subPosition) => (
                                                          <a
                                                            key={
                                                              subPosition?.id
                                                            }
                                                            className="dropdown-item ml-4 py-2"
                                                            href="#"
                                                            onClick={(e) => {
                                                              e.preventDefault();
                                                              handlePosition(
                                                                subPosition,
                                                                Index,
                                                                careheading?.id,
                                                                item?.id,
                                                              );
                                                            }}
                                                          >
                                                            <small className="small-text">
                                                              {subPosition.name}{" "}
                                                              (
                                                              {
                                                                subPosition.abbreviation
                                                              }
                                                              )
                                                            </small>
                                                          </a>
                                                        ),
                                                      ),
                                                    ];
                                                  } else if (
                                                    position.position.name ===
                                                      "special_positions" &&
                                                    position.subPositions
                                                  ) {
                                                    return [
                                                      ...position.subPositions.map(
                                                        (subPosition, key) => (
                                                          <a
                                                            key={
                                                              subPosition?.id
                                                            }
                                                            className={`dropdown-item ml-1 py-2 ${
                                                              key === 0
                                                                ? "mt-3"
                                                                : ""
                                                            }`}
                                                            href="#"
                                                            onClick={(e) => {
                                                              e.preventDefault();
                                                              handlePosition(
                                                                subPosition,
                                                                Index,
                                                                careheading?.id,
                                                                item?.id,
                                                              );
                                                            }}
                                                          >
                                                            <small className="small-text">
                                                              {subPosition.name}{" "}
                                                              (
                                                              {
                                                                subPosition.abbreviation
                                                              }
                                                              )
                                                            </small>
                                                          </a>
                                                        ),
                                                      ),
                                                    ];
                                                  } else {
                                                    return (
                                                      <a
                                                        key={i}
                                                        className="dropdown-item ml-1 py-2"
                                                        href="#"
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          handlePosition(
                                                            position.position,
                                                            Index,
                                                            careheading?.id,
                                                            item?.id,
                                                          );
                                                        }}
                                                      >
                                                        {/* remember to add abbreviation */}
                                                        <small className="small-text">
                                                          {
                                                            position.position
                                                              .name
                                                          }{" "}
                                                          (
                                                          {
                                                            position.position
                                                              .abbreviation
                                                          }
                                                          )
                                                        </small>
                                                      </a>
                                                    );
                                                  }
                                                })}
                                              <div className="dropdown-divider" />
                                              {SPECIAL_ASSIGNEE_POSITIONS.map(
                                                (position) => (
                                                  <a
                                                    key={position.assigneeType}
                                                    className="dropdown-item ml-1 py-2"
                                                    href="#"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      handlePosition(
                                                        position,
                                                        Index,
                                                        careheading?.id,
                                                        item?.id,
                                                      );
                                                    }}
                                                  >
                                                    <small className="small-text">
                                                      {position.name} (
                                                      {position.abbreviation})
                                                    </small>
                                                  </a>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-2 px-1">
                                          <div className="dropdown">
                                            <button
                                              className="btn btn-block border px-1"
                                              type="button"
                                              id="dropdownMenuButton"
                                              data-toggle="dropdown"
                                              aria-expanded="false"
                                              style={{
                                                height: "37.3px",
                                                padding: "0 12px",
                                              }}
                                            >
                                              <div className="d-flex align-items-center justify-content-between">
                                                <small className="f-11">
                                                  {intervention &&
                                                  intervention.provider &&
                                                  intervention.provider
                                                    ?.lastName
                                                    ? `${intervention.provider?.lastName} ${intervention.provider?.firstName}`
                                                    : " Select Provider"}
                                                </small>
                                                <BsChevronDown />
                                              </div>
                                            </button>
                                            <div
                                              className="dropdown-menu small border-0 shadow"
                                              aria-labelledby="dropdownMenuButton"
                                            >
                                              <p className="pl-4 mb-0 pb-2">
                                                <small className="small-text">
                                                  {getProviderDropdownPrompt(
                                                    intervention?.position,
                                                    rowProviders,
                                                  )}
                                                </small>
                                              </p>
                                              {rowProviders
                                                .sort((a, b) =>
                                                  getPersonName(a).localeCompare(
                                                    getPersonName(b),
                                                  ),
                                                )
                                                .map((provider, key) => (
                                                  <a
                                                    key={key}
                                                    className="dropdown-item ml-4 py-2"
                                                    href="#"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      handleProvider(
                                                        provider,
                                                        Index,
                                                        careheading?.id,
                                                        item?.id,
                                                      );
                                                    }}
                                                  >
                                                    <small className="small-text">
                                                      {provider?.lastName}
                                                      {", "}
                                                      {provider?.firstName} (
                                                      {provider.position
                                                        ?.abbreviation}
                                                      )
                                                    </small>
                                                  </a>
                                                ))}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-2 p-0 my-0">
                                          <div className="form-group row">
                                            <label
                                              for="staticEmail"
                                              className="col-md-4 pr-1 col-form-label text-right line-1 mt-1"
                                            >
                                              <small className="f-8">
                                                Review <br /> By:
                                              </small>
                                            </label>
                                            <div className="col-md-8 pl-2 mt-3">
                                              <div style={{ height: "37.3px" }}>
                                                <InputDatePicker
                                                  date={
                                                    intervention &&
                                                    intervention.review_date
                                                      ? intervention.review_date
                                                      : ""
                                                  }
                                                  setDate={handleSetReviewDate}
                                                  intervention={Index}
                                                  heading={careheading?.id}
                                                  issueId={item?.id}
                                                  height="37.3px"
                                                  style={{ width: "120px" }}
                                                  minDate={new Date()}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-2 px-0 pl-2">
                                          <div className="d-flex align-items-center">
                                            <input
                                              type="text"
                                              placeholder="Comments"
                                              value={intervention.comment || ""}
                                              onChange={(e) =>
                                                handleInterventionComment(
                                                  e.target.value,
                                                  careheading?.id,
                                                  item?.id,
                                                  Index,
                                                )
                                              }
                                              className="form-control border f-11 text-center outline-none"
                                              style={{
                                                height: "37.3px",
                                                padding: "0 12px",
                                              }}
                                            />
                                            <button
                                              type="button"
                                              className="btn p-0 ml-2 d-flex align-items-center justify-content-center"
                                              title="Clear or remove intervention"
                                              onClick={() =>
                                                openClearRowModal(
                                                  () =>
                                                    handleClearOrRemoveIntervention(
                                                      careheading?.id,
                                                      item?.id,
                                                      Index,
                                                    ),
                                                  {
                                                    title:
                                                      "Are you sure you want to delete this Intervention?",
                                                    note: "This Intervention will be lost.",
                                                  },
                                                )
                                              }
                                            >
                                              <FiTrash2
                                                className="text_danger"
                                                size={ICON_SIZE}
                                              />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      );
                                    },
                                  )}
                                  <button
                                    onClick={() =>
                                      handleIntervention(
                                        item,
                                        i,
                                        careheading?.id,
                                        item?.id,
                                      )
                                    }
                                    className="btn pl-5 mt-0 pt-0 "
                                  >
                                    <div className="text_primary">
                                      <b>+</b>{" "}
                                      <small className="f-11">
                                        Another intervention
                                      </small>
                                    </div>
                                  </button>
                                </div>
                              )})}
                              <button
                                onClick={() =>
                                  handleFunction(careheading?.id)
                                }
                                className="btn pl-1"
                              >
                                <div className="mt-0 pt-0 text_primary">
                                  <b>+</b>{" "}
                                  <small className="f-12">
                                    Add another Functional Issue
                                  </small>
                                </div>
                              </button>
                            </AccordionItemPanel>
                          </AccordionItem>
                        </div>
                      ))}
                  </Accordion>
                </div>
              )}
            {!date && (
              <div className="d-flex flex-column align-items-center justify-content-center">
                <h4 className="fs-18 text_primary text-center font-weight-bold mt-4 pt-2 mb-2">
                  Choose Care Plan Creation Date
                </h4>
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <p className="text_small mb-0 text-center">
                    <span className="font-weight-bold">Note:</span> Can be
                    COLLABORATIVE or INDIVIDUAL Care Plan
                  </p>
                </div>
                <div style={{ width: "260px" }}>
                  <DatePicker
                    selected={chosenDate}
                    onChange={(selectedDate) => setChosenDate(selectedDate)}
                    customInput={<MeetingEntryDateInput />}
                    dateFormat="dd MMM yyyy"
                    maxDate={new Date()}
                  />
                  {chosenDate && (
                    <div className="d-flex justify-content-between mt-4">
                      <Link
                        to={`/hcp/patient/details/${patientId}`}
                        className="btn btn_outline_danger w-116 rounded f-13 h-35"
                      >
                        BACK
                      </Link>
                      <button
                        type="button"
                        className="btn btn-outline-primary w-116 rounded f-13 h-35"
                        onClick={() => setDate(chosenDate)}
                      >
                        CHOOSE
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {date ? (
        <FooterButton
          child1={<Item1 onClear={handleClearAction} />}
          child2={
            <Item2
              props={props}
              date={date}
              handleCarePlan={handleSubmitCareplan}
              functions={functionIssue}
              carePlan={carePlan}
              type={type}
              meeting={meeting}
              meetingTypeId={meetingTypeId}
              attendees={attendees}
              onSaveForLater={handleSaveForLaterAction}
              onReview={handleReviewAction}
              carePlanId={reviewCarePlanId}
              editReason={editReason}
              carePlanStatus={reviewCarePlanStatus}
              carePlanVersion={reviewCarePlanVersion}
              fromTeamScores={fromTeamScores}
              teamScoreFromDate={teamScoreFromDate}
              teamScoreToDate={teamScoreToDate}
            />
          }
        />
      ) : (
        <Footer />
      )}

      <TeamScoresOverlay
        isOpen={showTeamScoreOverlay}
        onClose={() => setShowTeamScoreOverlay(false)}
        startLabel={teamScoreRange.start.format("DD MMM YYYY")}
        endLabel={teamScoreRange.end.format("DD MMM YYYY")}
        headings={teamScoreData?.data || []}
        isLoading={isTeamScoreLoading}
        error={teamScoreError}
      />

      <LatestFunctionalScoresOverlay
        isOpen={showLatestScoresOverlay}
        onClose={() => setShowLatestScoresOverlay(false)}
        headings={functionalScoresWithTeamScores?.data || []}
        isLoading={isLatestScoresLoading}
        error={latestScoresError}
        startLabel={moment(functionalScoresOverlayRange.startDate).format(
          "D MMM YYYY",
        )}
        endLabel={moment(functionalScoresOverlayRange.endDate).format(
          "D MMM YYYY",
        )}
      />

      {/* Navigation Confirmation Modal */}
      <div
        className={`modal fade ${showNavigationModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showNavigationModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={40}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Warning
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Are you sure you want to leave this screen?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> All data will be lost
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelNavigation}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmNavigation}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Switching care plan type — confirm before the rows are cleared */}
      <div
        className={`modal fade ${pendingType ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: pendingType ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={40}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Change Care Plan Type?
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                You are switching from <b>{type?.label}</b> to{" "}
                <b>{pendingType?.label}</b>.
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> Every functional issue, score and comment already
                entered on this plan will be cleared and the plan will start
                again.
              </p>
              {typeChangeCaveat && (
                <p className="text_default fs-13">{typeChangeCaveat}</p>
              )}
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={() => setPendingType(null)}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmTypeChange}
              >
                <small>YES, SWITCH</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Collaborative plan started by hand — send the user to assign team
          scores first */}
      <div
        className={`modal fade ${
          showTeamScoreRedirectModal ? "show d-block" : ""
        }`}
        tabIndex="-1"
        style={{
          backgroundColor: showTeamScoreRedirectModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaInfoCircle size={40} className="text_primary mb-2" />
                <h5 className="modal-title text_primary font-weight-bold">
                  Team Scores Needed
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                A Collaborative Care Plan is developed from the team scores for
                the case conference period.
              </p>
              <p className="text_default fs-13">
                We are taking you to <b>Functional Summaries</b> to assign the
                team scores. From there, saving them brings you back to create
                the care plan.
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={() => setShowTeamScoreRedirectModal(false)}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleGoToTeamScores}
              >
                <small>CONTINUE</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Clear Button Confirmation Modal */}
      <div
        className={`modal fade ${showClearModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showClearModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={40}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Warning
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Are you sure you want to clear this screen?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> All care plan data will be lost
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelClear}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmClear}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showClearRowModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showClearRowModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ width: "395px", maxWidth: "395px" }}
        >
          <div
            className="modal-content"
            style={{
              height: "207px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="modal-header border-0 mt-3 p-0"
              style={{ position: "relative" }}
            >
              <h5 className="modal-title text_primary font-weight-bold w-100 text-center m-0">
                Warning
              </h5>
              <button
                type="button"
                className="close"
                onClick={handleCancelClearRow}
                aria-label="Close"
                style={{ position: "absolute", right: 0, top: 0 }}
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div
              className="modal-body text-center p-0"
              style={{
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p className="text_default fs-13 mb-1">
                {clearRowModalContent.title}
              </p>
              <p className="text_default fs-13 mb-0">
                <b>NOTE:</b> {clearRowModalContent.note}
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between p-0">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelClearRow}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmClearRow}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CareManagement;
